(() => {
  // Preview refresh trigger for the order message flow: keep this tiny source bump for Shopify asset rebuilds.
  const STORAGE_KEY = 'avoidbit_order_tracker_session_v1';
  const STAGE_MIN = 1;
  const STAGE_MAX = 6;
  const ORDER_TRACKER_NOTE_PREFIX = 'ot:';

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function normalizeOrderNumber(value) {
    return (value || '').trim().toUpperCase();
  }

  function normalizeContact(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (raw.includes('@')) return raw.toLowerCase();
    const digits = raw.replace(/\D/g, '');
    return digits;
  }

  function extractEmailAddress(value) {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw || !raw.includes('@')) return '';
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(raw) ? raw : '';
  }

  function sanitizeReplyMessage(value) {
    const stripped = String(value || '')
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, ' ')
      .replace(/[<>]/g, '')
      .replace(/\r\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/[ \t]{2,}/g, ' ')
      .trim();

    return stripped.slice(0, 1000);
  }

  function parseResponseJSON(response) {
    return response
      .json()
      .catch(() => ({}));
  }

  function readStoredSession() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      if (!parsed.orderNumber) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  }

  function writeStoredSession(session) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  }

  function clearStoredSession() {
    sessionStorage.removeItem(STORAGE_KEY);
  }

  // ─── Colorizer helpers (used by conveyor belt) ───────────────────────────

  let colorParseCanvasCtx = null;

  function cssColorToRgb(colorValue) {
    const raw = String(colorValue || '').trim();
    if (!raw) return null;
    if (window.CSS && typeof window.CSS.supports === 'function' && !window.CSS.supports('color', raw)) {
      return null;
    }

    if (!colorParseCanvasCtx) {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      colorParseCanvasCtx = canvas.getContext('2d');
    }
    if (!colorParseCanvasCtx) return null;

    colorParseCanvasCtx.fillStyle = '#000000';
    colorParseCanvasCtx.fillStyle = raw;
    const normalized = colorParseCanvasCtx.fillStyle;

    if (normalized.startsWith('#')) {
      const hex = normalized.slice(1);
      if (hex.length === 3) {
        return [
          parseInt(hex[0] + hex[0], 16),
          parseInt(hex[1] + hex[1], 16),
          parseInt(hex[2] + hex[2], 16),
        ];
      }
      if (hex.length === 6) {
        return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
      }
    }

    const match = normalized.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (!match) return null;
    return [Number(match[1]), Number(match[2]), Number(match[3])];
  }

  function hexToRgb(hex) {
    const parsedCss = cssColorToRgb(hex);
    if (parsedCss) return parsedCss;

    const v = String(hex || '').replace('#', '').trim();
    if (v.length === 3) {
      return [parseInt(v[0] + v[0], 16), parseInt(v[1] + v[1], 16), parseInt(v[2] + v[2], 16)];
    }
    if (v.length === 6) {
      return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
    }
    return [128, 128, 128];
  }

  function maxChannelDelta(r, g, b) {
    return Math.max(Math.abs(r - g), Math.abs(g - b), Math.abs(r - b));
  }

  function isNearWhite(r, g, b) {
    return r > 242 && g > 242 && b > 242;
  }

  function loadImageCors(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Cannot load: ${src}`));
      img.src = src;
    });
  }

  async function renderColorizedCanvas(baseSrc, maskSrc, primaryHex, secondaryHex, backgroundSrc = '', backgroundColor = '') {
    const [baseImg, maskImg] = await Promise.all([
      loadImageCors(baseSrc),
      maskSrc ? loadImageCors(maskSrc).catch(() => null) : Promise.resolve(null),
    ]);

    // Run colorization at full image resolution, then downscale only for conveyor display.
    const w = Math.max(1, Math.round(baseImg.naturalWidth));
    const h = Math.max(1, Math.round(baseImg.naturalHeight));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    const baseCanvas = document.createElement('canvas');
    baseCanvas.width = w;
    baseCanvas.height = h;
    const baseCtx = baseCanvas.getContext('2d', { willReadFrequently: true });

    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = w;
    maskCanvas.height = h;
    const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true });

    // Tracker conveyor should remain cutout-only with transparent background.
    const bgPixels = null;

    if (!baseCtx || !maskCtx) {
      throw new Error('Unable to initialize image buffers.');
    }

    baseCtx.drawImage(baseImg, 0, 0, w, h);
    if (maskImg) {
      maskCtx.drawImage(maskImg, 0, 0, w, h);
    }

    const baseData = baseCtx.getImageData(0, 0, w, h).data;
    const maskData = maskImg ? maskCtx.getImageData(0, 0, w, h).data : null;
    const primary = hexToRgb(primaryHex);
    const secondary = hexToRgb(secondaryHex);
    const primaryLuma = (primary[0] + primary[1] + primary[2]) / 765;
    const secondaryLuma = (secondary[0] + secondary[1] + secondary[2]) / 765;
    const minTintStrength = 0.72;
    const primaryStrength = minTintStrength + (1 - minTintStrength) * primaryLuma;
    const secondaryStrength = minTintStrength + (1 - minTintStrength) * secondaryLuma;
    const output = ctx.createImageData(w, h);
    const b = baseData;
    const m = maskData;
    const o = output.data;

    for (let i = 0; i < b.length; i += 4) {
      const br = b[i];
      const bg = b[i + 1];
      const bb = b[i + 2];
      const ba = b[i + 3] / 255;

      if (ba <= 0) {
        if (bgPixels) {
          o[i] = bgPixels[i];
          o[i + 1] = bgPixels[i + 1];
          o[i + 2] = bgPixels[i + 2];
          o[i + 3] = 255;
        } else {
          o[i] = 0;
          o[i + 1] = 0;
          o[i + 2] = 0;
          o[i + 3] = 0;
        }
        continue;
      }

      const lum = (br + bg + bb) / 765;
      let pMask = 0;
      let sMask = 0;

      if (m) {
        const mr = m[i];
        const mg = m[i + 1];
        const ma = m[i + 3] / 255;

        // Match product colorizer mask convention exactly:
        // red channel = primary, green channel = secondary.
        pMask = (mr / 255) * ma;
        sMask = (mg / 255) * ma;

        if (pMask < 0.01) pMask = 0;
        if (sMask < 0.01) sMask = 0;

      } else {
        const delta = maxChannelDelta(br, bg, bb);
        const neutral = delta < 18;
        if (neutral && lum > 0.82 && !isNearWhite(br, bg, bb)) {
          sMask = 1;
        } else if (neutral && lum > 0.18 && lum <= 0.82) {
          pMask = 1;
        }
      }

      pMask *= primaryStrength;
      sMask *= secondaryStrength;

      const maskTotal = pMask + sMask;
      if (maskTotal > 1) {
        pMask /= maskTotal;
        sMask /= maskTotal;
      }
      const keep = Math.max(0, 1 - pMask - sMask);

      const pR = (br * primary[0]) / 255;
      const pG = (bg * primary[1]) / 255;
      const pB = (bb * primary[2]) / 255;
      const sR = (br * secondary[0]) / 255;
      const sG = (bg * secondary[1]) / 255;
      const sB = (bb * secondary[2]) / 255;

      const outR = Math.round(br * keep + pR * pMask + sR * sMask);
      const outG = Math.round(bg * keep + pG * pMask + sG * sMask);
      const outB = Math.round(bb * keep + pB * pMask + sB * sMask);

      if (bgPixels) {
        const invAlpha = 1 - ba;
        o[i] = Math.round(outR * ba + bgPixels[i] * invAlpha);
        o[i + 1] = Math.round(outG * ba + bgPixels[i + 1] * invAlpha);
        o[i + 2] = Math.round(outB * ba + bgPixels[i + 2] * invAlpha);
        o[i + 3] = 255;
      } else {
        o[i] = outR;
        o[i + 1] = outG;
        o[i + 2] = outB;
        o[i + 3] = b[i + 3];
      }
    }

    ctx.putImageData(output, 0, 0);

    const MAX_H = 1600;
    const displayScale = Math.min(1, MAX_H / h);
    if (displayScale >= 1) {
      return canvas;
    }

    const displayCanvas = document.createElement('canvas');
    displayCanvas.width = Math.max(1, Math.round(w * displayScale));
    displayCanvas.height = Math.max(1, Math.round(h * displayScale));
    const displayCtx = displayCanvas.getContext('2d');
    if (displayCtx) {
      displayCtx.imageSmoothingEnabled = true;
      displayCtx.imageSmoothingQuality = 'high';
      displayCtx.drawImage(canvas, 0, 0, displayCanvas.width, displayCanvas.height);
      return displayCanvas;
    }

    return canvas;
  }

  async function renderCutoutCanvas(imageSrc) {
    const img = await loadImageCors(imageSrc);
    const MAX_H = 1600;
    const scale = Math.min(1, MAX_H / img.naturalHeight);
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    return canvas;
  }

  function renderPlaceholderCanvas(primaryHex, secondaryHex, label) {
    const w = 120, h = 160;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');

    const grad = ctx.createLinearGradient(0, 0, w, h);
    grad.addColorStop(0, primaryHex || '#594abd');
    grad.addColorStop(1, secondaryHex || '#291e62');
    ctx.fillStyle = grad;
    ctx.beginPath();
    if (ctx.roundRect) {
      ctx.roundRect(10, 8, w - 20, h - 20, 10);
    } else {
      ctx.rect(10, 8, w - 20, h - 20);
    }
    ctx.fill();

    ctx.fillStyle = 'rgba(255,255,255,0.12)';
    ctx.beginPath();
    if (ctx.roundRect) {
      ctx.roundRect(10, 8, w - 20, (h - 20) / 2, [10, 10, 0, 0]);
    } else {
      ctx.rect(10, 8, w - 20, (h - 20) / 2);
    }
    ctx.fill();

    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    ctx.font = 'bold 11px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText((label || '').slice(0, 10), w / 2, h / 2 - 2);

    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.font = '9px system-ui, sans-serif';
    ctx.fillText('AvoidBits', w / 2, h / 2 + 13);

    return canvas;
  }

  function canvasToCroppedDataUrl(canvas, paddingRatio = 0.06) {
    const croppedCanvas = cropCanvas(canvas, paddingRatio);
    return croppedCanvas ? croppedCanvas.toDataURL('image/png') : '';
  }

  function cropCanvas(canvas, paddingRatio = 0.06) {
    if (!canvas) return null;

    const width = Math.max(1, Math.round(canvas.width || 0));
    const height = Math.max(1, Math.round(canvas.height || 0));
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return canvas;

    const { data } = ctx.getImageData(0, 0, width, height);
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha > 8) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX < 0 || maxY < 0) {
      return canvas;
    }

    const padX = Math.round(width * paddingRatio);
    const padY = Math.round(height * paddingRatio);
    const cropLeft = Math.max(0, minX - padX);
    const cropTop = Math.max(0, minY - padY);
    const cropRight = Math.min(width, maxX + padX + 1);
    const cropBottom = Math.min(height, maxY + padY + 1);
    const cropWidth = Math.max(1, cropRight - cropLeft);
    const cropHeight = Math.max(1, cropBottom - cropTop);

    const croppedCanvas = document.createElement('canvas');
    croppedCanvas.width = cropWidth;
    croppedCanvas.height = cropHeight;
    const croppedCtx = croppedCanvas.getContext('2d');
    if (!croppedCtx) {
      return canvas;
    }

    croppedCtx.drawImage(canvas, cropLeft, cropTop, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
    return croppedCanvas;
  }

  function resizeCanvasToMaxHeight(canvas, maxHeight) {
    if (!canvas) return null;

    const sourceWidth = Math.max(1, Math.round(canvas.width || 0));
    const sourceHeight = Math.max(1, Math.round(canvas.height || 0));
    const cappedHeight = Math.max(1, Math.round(maxHeight || 0));
    if (!cappedHeight || sourceHeight <= cappedHeight) {
      return canvas;
    }

    const scale = Math.min(1, cappedHeight / sourceHeight);
    const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
    const targetHeight = Math.max(1, Math.round(sourceHeight * scale));

    const resizedCanvas = document.createElement('canvas');
    resizedCanvas.width = targetWidth;
    resizedCanvas.height = targetHeight;
    const resizedCtx = resizedCanvas.getContext('2d');
    if (!resizedCtx) {
      return canvas;
    }

    resizedCtx.imageSmoothingEnabled = true;
    resizedCtx.imageSmoothingQuality = 'high';
    resizedCtx.drawImage(canvas, 0, 0, targetWidth, targetHeight);
    return resizedCanvas;
  }

  function safeLower(value) {
    return String(value || '').trim().toLowerCase();
  }

  function toHexToken(value, fallback) {
    const token = String(value || '').trim();
    if (!token) return fallback;

    if (/^#[0-9a-f]{3,8}$/i.test(token)) return token;

    const rgb = cssColorToRgb(token);
    if (rgb) {
      const toByteHex = (n) => Math.max(0, Math.min(255, Number(n) || 0)).toString(16).padStart(2, '0');
      return `#${toByteHex(rgb[0])}${toByteHex(rgb[1])}${toByteHex(rgb[2])}`;
    }

    const fallbackRgb = cssColorToRgb(fallback);
    if (fallbackRgb) {
      const toByteHex = (n) => Math.max(0, Math.min(255, Number(n) || 0)).toString(16).padStart(2, '0');
      return `#${toByteHex(fallbackRgb[0])}${toByteHex(fallbackRgb[1])}${toByteHex(fallbackRgb[2])}`;
    }

    return fallback;
  }

  function pickString(record, keys, fallback = '') {
    if (!record || typeof record !== 'object') return fallback;
    for (const key of keys) {
      const value = record[key];
      if (value === null || typeof value === 'undefined') continue;
      const token = String(value).trim();
      if (token) return token;
    }
    return fallback;
  }

  function pickNumber(record, keys, fallback = 0) {
    if (!record || typeof record !== 'object') return fallback;
    for (const key of keys) {
      const value = Number(record[key]);
      if (Number.isFinite(value)) return value;
    }
    return fallback;
  }

  function extractOrderItemsFromPayload(payload) {
    if (!payload || typeof payload !== 'object') return [];

    // Preferred contract: payload.order.items
    if (Array.isArray(payload.order?.items)) return payload.order.items;

    // Controlled compatibility aliases (legacy payloads)
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.orderItems)) return payload.orderItems;
    if (Array.isArray(payload.order_items)) return payload.order_items;
    if (Array.isArray(payload.lineItems)) return payload.lineItems;
    if (Array.isArray(payload.line_items)) return payload.line_items;
    if (Array.isArray(payload.order?.line_items)) return payload.order.line_items;

    return [];
  }

  function getVariantOptionValue(variant, index) {
    if (!variant || index < 0) return '';
    if (Array.isArray(variant.options) && typeof variant.options[index] !== 'undefined') {
      return variant.options[index] || '';
    }
    if (index === 0) return variant.option1 || '';
    if (index === 1) return variant.option2 || '';
    if (index === 2) return variant.option3 || '';
    return '';
  }

  function parseVariantId(urlValue) {
    try {
      const parsed = new URL(urlValue, window.location.origin);
      const token = parsed.searchParams.get('variant');
      return token ? String(token) : '';
    } catch (error) {
      return '';
    }
  }

  function selectorEscape(value) {
    const token = String(value || '');
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(token);
    }
    return token.replace(/["\\]/g, '\\$&');
  }

  function normalizeKey(value) {
    return String(value || '').trim().toLowerCase();
  }

  function normalizeOrderItems(rawItems) {
    if (!Array.isArray(rawItems)) return [];

    return rawItems.map((item, index) => {
      const title = pickString(item, ['title', 'name', 'product_title', 'productTitle'], `Item ${index + 1}`);

      const quantityRaw = pickNumber(item, ['quantity', 'qty', 'count'], 1);
      const quantity = Number.isFinite(quantityRaw) && quantityRaw > 0 ? Math.round(quantityRaw) : 1;

      const colorizerUrl = pickString(item, ['colorizerUrl', 'productUrl', 'product_url', 'url'], '');

      // Optional nested structure supported by contract:
      // selectedColors: { primary: '#xxxxxx', secondary: '#yyyyyy' }
      const nestedPrimary = pickString(item?.selectedColors, ['primary', 'primaryColor', 'color'], '');
      const nestedSecondary = pickString(item?.selectedColors, ['secondary', 'secondaryColor', 'accent'], '');

      const rawPrimaryColor = pickString(item, ['primaryColor', 'primary_color'], nestedPrimary);
      const hasExplicitPrimaryColor = Boolean(rawPrimaryColor);
      const primaryColor = toHexToken(rawPrimaryColor, '#594abd');

      const rawSecondaryColor = pickString(item, ['secondaryColor', 'secondary_color'], nestedSecondary);
      const hasExplicitSecondaryColor = Boolean(rawSecondaryColor);
      const secondaryColor = toHexToken(rawSecondaryColor, '#291e62');

      const baseSrc = pickString(item, ['baseSrc', 'base_src', 'baseImage', 'base_image'], '');
      const maskSrc = pickString(item, ['maskSrc', 'mask_src', 'maskImage', 'mask_image'], '');

      return {
        title,
        quantity,
        sku: pickString(item, ['sku'], ''),
        variantTitle: pickString(item, ['variantTitle', 'variant_title'], ''),
        primaryColor,
        secondaryColor,
        hasExplicitPrimaryColor,
        hasExplicitSecondaryColor,
        baseSrc,
        maskSrc,
        colorizerUrl,
        variantId: parseVariantId(colorizerUrl),
      };
    });
  }

  async function resolveColorizerFromProductUrl(colorizerUrl, fallbackPrimary, fallbackSecondary, options = {}) {
    if (!colorizerUrl) return null;

    try {
      const response = await fetch(colorizerUrl, { credentials: 'same-origin' });
      if (!response.ok) return null;
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const root = doc.querySelector('[data-product-colorizer-selector]');
      if (!root) return null;

      let baseSrc = root.getAttribute('data-base-src') || '';
      let maskSrc = root.getAttribute('data-mask-src') || '';
      const backgroundSrc = root.getAttribute('data-background-src') || '';
      const backgroundColor = root.getAttribute('data-background-color') || '';
      const primaryFallback = fallbackPrimary || '#594abd';
      const secondaryFallback = fallbackSecondary || '#291e62';
      const hasExplicitPrimary = Boolean(options.lockPrimary);
      const hasExplicitSecondary = Boolean(options.lockSecondary);

      let primaryColor = primaryFallback;
      let secondaryColor = secondaryFallback;

      const variantId = parseVariantId(colorizerUrl);
      const variantsNode = doc.querySelector('[data-colorizer-prototype-variants]');
      const optionNamesNode = doc.querySelector('[data-colorizer-prototype-option-names]');
      const swatchButtons = [...doc.querySelectorAll('[data-colorizer-prototype-value][data-channel]')];
      const podButtons = [...doc.querySelectorAll('[data-colorizer-prototype-pod]')];
      const podMapElement = doc.querySelector('[data-colorizer-prototype-pod-map]');

      let podMediaMap = {};
      if (podMapElement) {
        try {
          const parsed = JSON.parse(podMapElement.textContent || '[]');
          podMediaMap = parsed.reduce((acc, entry) => {
            const podKey = normalizeKey(entry?.pod);
            if (podKey && entry?.base) {
              acc[podKey] = {
                base: entry.base,
                mask: entry.mask || '',
              };
            }
            return acc;
          }, {});
        } catch (error) {
          podMediaMap = {};
        }
      }

      const podButtonMediaByKey = podButtons.reduce((acc, btn) => {
        const podKey = normalizeKey(btn.dataset.optionValue || '');
        if (!podKey) return acc;
        acc[podKey] = {
          base: btn.dataset.podBaseSrc || '',
          mask: btn.dataset.podMaskSrc || '',
        };
        return acc;
      }, {});

      const swatchColorFor = (optionName, optionValue, channel) => {
        const optionNameKey = safeLower(optionName);
        const optionValueKey = safeLower(optionValue);
        const match = swatchButtons.find((button) => {
          return safeLower(button.dataset.optionName || '') === optionNameKey
            && safeLower(button.dataset.optionValue || '') === optionValueKey
            && safeLower(button.dataset.channel || '') === safeLower(channel || '');
        });

        return match?.dataset?.swatchColor || '';
      };

      const checkedSwatches = swatchButtons.filter((button) => button.getAttribute('aria-checked') === 'true');
      const checkedPrimary = checkedSwatches.find((button) => safeLower(button.dataset.channel || '') === 'primary');
      const checkedSecondary = checkedSwatches.find((button) => safeLower(button.dataset.channel || '') === 'secondary');

      if (variantId && variantsNode && optionNamesNode) {
        let variants = [];
        let optionNames = [];
        try {
          variants = JSON.parse(variantsNode.textContent || '[]');
          optionNames = JSON.parse(optionNamesNode.textContent || '[]');
        } catch (error) {
          variants = [];
          optionNames = [];
        }

        const variant = variants.find((entry) => String(entry?.id || '') === variantId);
        if (variant && Array.isArray(optionNames)) {
          const podIndex = optionNames.findIndex((name) => normalizeKey(name) === 'pod');
          const primaryIndex = optionNames.findIndex((name) => {
            const token = safeLower(name);
            return token.includes('color') && !token.includes('secondary');
          });

          const secondaryIndex = optionNames.findIndex((name) => safeLower(name).includes('secondary color'));
          const podValue = getVariantOptionValue(variant, podIndex);
          const podKey = normalizeKey(podValue);

          if (podKey) {
            const podButtonMedia = podButtonMediaByKey[podKey];
            const podMapMedia = podMediaMap[podKey];
            const podMedia = (podButtonMedia && podButtonMedia.base) ? podButtonMedia : podMapMedia;
            if (podMedia) {
              if (podMedia.base) {
                baseSrc = podMedia.base;
              }
              if (podMedia.mask) {
                maskSrc = podMedia.mask;
              }
            }
          }

          const primaryValue = getVariantOptionValue(variant, primaryIndex);
          const secondaryValue = getVariantOptionValue(variant, secondaryIndex);

          if (!hasExplicitPrimary && primaryValue) {
            const primarySwatch = doc.querySelector(
              `[data-colorizer-prototype-value][data-channel="primary"][data-option-value="${selectorEscape(primaryValue)}"]`
            );
            if (primarySwatch?.dataset?.swatchColor) {
              primaryColor = primarySwatch.dataset.swatchColor;
            }
          }

          if (!hasExplicitSecondary && secondaryValue) {
            const secondarySwatch = doc.querySelector(
              `[data-colorizer-prototype-value][data-channel="secondary"][data-option-value="${selectorEscape(secondaryValue)}"]`
            );
            if (secondarySwatch?.dataset?.swatchColor) {
              secondaryColor = secondarySwatch.dataset.swatchColor;
            }
          }
        }
      }

      if (!hasExplicitPrimary && checkedPrimary?.dataset?.swatchColor) {
        primaryColor = checkedPrimary.dataset.swatchColor;
      }
      if (!hasExplicitSecondary && checkedSecondary?.dataset?.swatchColor) {
        secondaryColor = checkedSecondary.dataset.swatchColor;
      }

      if (!hasExplicitPrimary && (!primaryColor || primaryColor === primaryFallback) && swatchButtons.length) {
        const primaryOptionName = optionNamesNode ? JSON.parse(optionNamesNode.textContent || '[]').find((name) => {
          const token = safeLower(name);
          return token.includes('color') && !token.includes('secondary');
        }) : '';
        const primaryOptionValue = checkedPrimary?.dataset?.optionValue || '';
        const swatchPrimary = swatchColorFor(primaryOptionName || checkedPrimary?.dataset?.optionName || '', primaryOptionValue, 'primary');
        if (swatchPrimary) primaryColor = swatchPrimary;
      }

      if (!hasExplicitSecondary && (!secondaryColor || secondaryColor === secondaryFallback) && swatchButtons.length) {
        const secondaryOptionValue = checkedSecondary?.dataset?.optionValue || '';
        const swatchSecondary = swatchColorFor(checkedSecondary?.dataset?.optionName || 'Secondary Color', secondaryOptionValue, 'secondary');
        if (swatchSecondary) secondaryColor = swatchSecondary;
      }

      return {
        baseSrc,
        maskSrc,
        backgroundSrc,
        backgroundColor,
        primaryColor: toHexToken(primaryColor, primaryFallback),
        secondaryColor: toHexToken(secondaryColor, secondaryFallback),
      };
    } catch (error) {
      return null;
    }
  }

  async function initTracker(root) {
    function repairMalformedTrackerRoot(targetRoot) {
      if (!targetRoot) return;

      // Remove malformed attributes that appeared when broken SVG lines leaked into the <section> tag.
      ['<lineargradient', 'x1', 'y1', 'x2', 'y2'].forEach((attr) => {
        if (targetRoot.hasAttribute(attr)) targetRoot.removeAttribute(attr);
      });

      // Remove leaked text fragments rendered as text nodes anywhere inside the section.
      const shouldStripLeakedText = (textValue) => {
        const text = String(textValue || '').trim().toLowerCase();
        if (!text) return true;
        return (
          text.includes('data-demo-order-number=') ||
          text.includes('data-demo-pin=') ||
          text.includes('data-prefill-order-number=') ||
          text.includes('<lineargradient')
        );
      };

      const walker = document.createTreeWalker(targetRoot, NodeFilter.SHOW_TEXT);
      const textNodesToRemove = [];
      while (walker.nextNode()) {
        const current = walker.currentNode;
        if (shouldStripLeakedText(current.textContent)) {
          textNodesToRemove.push(current);
        }
      }

      textNodesToRemove.forEach((node) => node.remove());
    }

    repairMalformedTrackerRoot(root);

    const form = root.querySelector('[data-tracker-form]');
    const gate = root.querySelector('[data-tracker-gate]');
    const panel = root.querySelector('[data-tracker-panel]');
    const orderInput = root.querySelector('[data-tracker-order-input]');
    const contactInput = root.querySelector('[data-tracker-contact-input]');
    const accessCodeInput = root.querySelector('[data-tracker-access-code-input]');
    const gateStatus = root.querySelector('[data-tracker-gate-status]');
    const orderDisplay = root.querySelector('[data-tracker-order-display]');
    const stages = [...root.querySelectorAll('[data-tracker-stages] [data-stage]')];
    const trackFill = root.querySelector('[data-tracker-track-fill]');
    const trackBar  = root.querySelector('[data-tracker-trackbar]');
    const printPctEl = root.querySelector('[data-tracker-print-pct]');
    const stagDetailEl = root.querySelector('[data-tracker-stage-detail]');
    const stageVisualEl = root.querySelector('[data-tracker-stage-visual]');
    let leftStageVideoEl = root.querySelector('[data-stage-box-video="left"]');
    let rightStageVideoEl = root.querySelector('[data-stage-box-video="right"]');
    const stageVideoConfigNodes = [...root.querySelectorAll('[data-stage-box-video-config]')];
    const customerMessage = root.querySelector('[data-tracker-customer-message]');
    const orderItemsList = root.querySelector('[data-tracker-order-items]');
    const orderItemsCount = root.querySelector('[data-tracker-order-items-count]');
    const orderItemsStatus = root.querySelector('[data-tracker-order-items-status]');
    const previewImage = root.querySelector('[data-tracker-order-preview-image]');
    const previewPlaceholder = root.querySelector('[data-tracker-order-preview-placeholder]');
    const endSessionButton = root.querySelector('[data-tracker-end-session]');

    if (!form || !gate || !panel || !orderInput || !contactInput || !accessCodeInput || !gateStatus || !stages.length) {
      return;
    }

    const requestPinEndpoint = (root.dataset.requestPinEndpoint || '').trim();
    const authEndpoint = (root.dataset.authEndpoint || '').trim();
    const statusEndpoint = (root.dataset.statusEndpoint || '').trim();
    const orderItemsEndpoint = (root.dataset.orderItemsEndpoint || '').trim();
    const variantRenderEndpoint = (root.dataset.variantRenderEndpoint || '').trim();
    const replyHistoryEndpoint = (root.dataset.replyHistoryEndpoint || '').trim();
    const replySendEndpoint = (root.dataset.replySendEndpoint || '').trim();
    const prefillOrderNumber = normalizeOrderNumber(root.dataset.prefillOrderNumber || '');
    const requestPinButton = root.querySelector('[data-tracker-request-pin]');
    function ensureStageSideVideo(side) {
      const selector = side === 'left'
        ? '.order-tracker__stage-visual-side--left'
        : '.order-tracker__stage-visual-side--right';
      const sideEl = root.querySelector(selector);
      if (!sideEl) return null;

      let videoEl = sideEl.querySelector('[data-stage-box-video]');
      if (videoEl) return videoEl;

      videoEl = document.createElement('video');
      videoEl.className = 'order-tracker__stage-side-video';
      videoEl.dataset.stageBoxVideo = side;
      videoEl.muted = true;
      videoEl.loop = true;
      videoEl.controls = false;
      videoEl.autoplay = true;
      videoEl.playsInline = true;
      videoEl.preload = 'metadata';
      sideEl.appendChild(videoEl);
      return videoEl;
    }

    function buildVideoCandidates(sourceUrl) {
      const source = String(sourceUrl || '').trim();
      if (!source) return [];

      const candidates = [];
      const push = (value) => {
        const v = String(value || '').trim();
        if (!v) return;
        if (!candidates.includes(v)) candidates.push(v);
      };

      if (source.includes('/files/')) {
        push(source.replace('/files/', '/cdn/shop/files/'));
        push(source);
      } else {
        push(source);
      }
      if (source.includes('/cdn/shop/files/')) {
        push(source.replace('/cdn/shop/files/', '/files/'));
      }

      if (!/\.mp4(\?|$)/i.test(source)) {
        push(`${source}.mp4`);
        if (source.includes('/files/')) push(`${source.replace('/files/', '/cdn/shop/files/')}.mp4`);
        if (source.includes('/cdn/shop/files/')) push(`${source.replace('/cdn/shop/files/', '/files/')}.mp4`);
      }

      return candidates;
    }

    function attachVideoFailover(videoEl) {
      if (!videoEl || videoEl.dataset.failoverBound === 'true') return;
      videoEl.dataset.failoverBound = 'true';

      videoEl.addEventListener('error', () => {
        const queue = (videoEl.dataset.candidateQueue || '')
          .split('|')
          .map((entry) => entry.trim())
          .filter(Boolean);

        if (!queue.length) return;

        const nextSrc = queue.shift();
        videoEl.dataset.candidateQueue = queue.join('|');
        videoEl.src = nextSrc;
        videoEl.dataset.currentSrc = nextSrc;
        videoEl.load();

        const retryPlay = videoEl.play();
        if (retryPlay && typeof retryPlay.catch === 'function') {
          retryPlay.catch(() => {});
        }
      });
    }

    leftStageVideoEl = leftStageVideoEl || ensureStageSideVideo('left');
    rightStageVideoEl = rightStageVideoEl || ensureStageSideVideo('right');
    attachVideoFailover(leftStageVideoEl);
    attachVideoFailover(rightStageVideoEl);

    const defaultStageBoxVideos = {
      left: (root.dataset.leftBoxVideoDefault || '').trim() || 'https://cdn.shopify.com/videos/c/o/v/eaf232f0f1ad4acabe3a47397a733236.mp4',
      right: (root.dataset.rightBoxVideoDefault || '').trim() || 'https://cdn.shopify.com/videos/c/o/v/eaf232f0f1ad4acabe3a47397a733236.mp4',
    };

    const fallbackStage = clamp(Number(root.dataset.currentStage || STAGE_MIN), STAGE_MIN, STAGE_MAX);
    const fallbackProgress = clamp(Number(root.dataset.printProgress || 0), 0, 100);
    const STATUS_POLL_INTERVAL_MS = 60000;

    let activeSession = null;
    let currentOrderItems = [];
    let selectedOrderIndex = -1;
    let statusPollTimer = null;
    let statusPollInFlight = false;
    const stageBoxVideosByStage = {};

    stageVideoConfigNodes.forEach((node) => {
      const stage = clamp(Number(node.dataset.stage || STAGE_MIN), STAGE_MIN, STAGE_MAX);
      const side = String(node.dataset.side || '').trim().toLowerCase();
      const src = String(node.dataset.src || '').trim();
      if (!src || (side !== 'left' && side !== 'right')) return;
      if (!stageBoxVideosByStage[stage]) stageBoxVideosByStage[stage] = {};
      stageBoxVideosByStage[stage][side] = src;
    });

    const INTERNAL_ACCESS_CODE = '1111';

    function getInternalOrderRecord(orderNumber) {
      const records = {
        '1278': {
          contacts: ['hank.newman@outlook.com'],
          stage: fallbackStage,
          printProgress: fallbackProgress,
          customerMessage:
            customerMessage?.innerHTML
            || '<p>Your order is in queue. We will post updates here as it moves through production.</p>',
          items: [
            {
              title: 'Launchpad++',
              quantity: 1,
              sku: 'launchpad++_hm0406',
              variantTitle: 'Silver / Red',
              colorizerUrl: 'https://avoidbit.com/products/launchpad-1?variant=50476463194341',
              primaryColor: '#c0c0c0',
              secondaryColor: '#ff0000',
            },
            {
              title: 'Launchpad++ [CUSTOM: Labels & Decals]',
              quantity: 1,
              sku: 'launchpad++_hm0406_custom',
              variantTitle: 'White / Violet',
              colorizerUrl: 'https://avoidbit.com/products/launchpad-1?variant=50476463194341',
              primaryColor: '#ffffff',
              secondaryColor: '#800080',
            },
          ],
        },
      };

      return records[orderNumber] || null;
    }

    function setGateStatus(message) {
      gateStatus.textContent = message || '';
    }

    function setPanelVisibility(isOpen) {
      panel.hidden = !isOpen;
      gate.hidden = isOpen;
    }

    function setSubmitting(isSubmitting) {
      const submitButton = form.querySelector('[data-tracker-submit]');
      if (submitButton) {
        submitButton.disabled = isSubmitting;
      }
      if (requestPinButton) {
        requestPinButton.disabled = isSubmitting;
      }
      orderInput.disabled = isSubmitting;
      contactInput.disabled = isSubmitting;
      accessCodeInput.disabled = isSubmitting;
    }

    async function requestAccessCode(orderNumber, contact) {
      const normalizedContact = normalizeContact(contact);

      if (!requestPinEndpoint) {
        throw new Error('Access-code delivery is not configured right now.');
      }

      const response = await fetch(new URL(requestPinEndpoint, window.location.origin).toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          orderNumber,
          contact: normalizedContact,
        }),
      });

      const data = await parseResponseJSON(response);
      if (!response.ok || data.ok === false) {
        throw new Error(data.message || 'We could not send an access code for this order.');
      }

      return data;
    }

    function setStageVideoSource(videoEl, sourceUrl) {
      if (!videoEl) return;

      videoEl.controls = false;
      videoEl.removeAttribute('controls');
      videoEl.loop = true;
      videoEl.autoplay = true;
      videoEl.muted = true;
      videoEl.playsInline = true;

      const source = String(sourceUrl || '').trim();
      const parentSide = videoEl.closest('.order-tracker__stage-visual-side');
      const candidates = buildVideoCandidates(source);

      if (!candidates.length) {
        videoEl.pause();
        videoEl.removeAttribute('src');
        videoEl.dataset.currentSrc = '';
        videoEl.dataset.candidateQueue = '';
        videoEl.load();
        if (parentSide) parentSide.classList.add('is-video-empty');
        return;
      }

      const primarySource = candidates[0];
      const fallbackQueue = candidates.slice(1);
      videoEl.dataset.candidateQueue = fallbackQueue.join('|');

      if (videoEl.dataset.currentSrc !== primarySource) {
        videoEl.src = primarySource;
        videoEl.dataset.currentSrc = primarySource;
        videoEl.load();
      }

      if (parentSide) parentSide.classList.remove('is-video-empty');

      const playResult = videoEl.play();
      if (playResult && typeof playResult.catch === 'function') {
        playResult.catch(() => {});
      }
    }

    function applyStageSideVideos(stage) {
      const normalizedStage = clamp(Number(stage || STAGE_MIN), STAGE_MIN, STAGE_MAX);
      const staged = stageBoxVideosByStage[normalizedStage] || {};

      setStageVideoSource(leftStageVideoEl, staged.left || defaultStageBoxVideos.left);
      setStageVideoSource(rightStageVideoEl, staged.right || defaultStageBoxVideos.right);
    }

    function applyTrackerVisuals(stage, printProgress) {
      const normalizedStage = clamp(Number(stage || STAGE_MIN), STAGE_MIN, STAGE_MAX);
      const pct = clamp(Number(printProgress || 0), 0, 100);

      // Stage node classes
      stages.forEach((item) => {
        const itemStage = Number(item.dataset.stage || STAGE_MIN);
        item.classList.remove('is-complete', 'is-active');
        if (itemStage < normalizedStage) {
          item.classList.add('is-complete');
        } else if (itemStage === normalizedStage) {
          item.classList.add('is-active');
        }
      });

      // Track fill — 5 segments for 6 nodes, each segment = 20%
      const segWidth = 100 / (STAGE_MAX - 1);
      let fillPct;
      if (normalizedStage <= 1) {
        fillPct = 0;
      } else {
        fillPct = segWidth * (normalizedStage - 1);
      }

      if (trackFill) trackFill.style.width = `${fillPct}%`;
      if (trackBar)  trackBar.setAttribute('aria-valuenow', String(Math.round(fillPct)));

      // Print % label inside stage 3 node info
      if (printPctEl) {
        printPctEl.textContent = '';
      }

      if (stageVisualEl) {
        stageVisualEl.dataset.stage = String(normalizedStage);
      }

      applyStageSideVideos(normalizedStage);

      // Detail strip below the rail
      if (stagDetailEl) {
        const detailMap = {
          1: 'AvoidBits has been notified — your order is confirmed.',
          2: 'Filament is being dried and prepared for print quality.',
          3: 'Printing in progress.',
          4: 'Print complete. Packaging your order now.',
          5: 'Your package is in transit.',
          6: 'Your order has been delivered!',
        };
        stagDetailEl.textContent = detailMap[normalizedStage] || '';
      }
    }

    function applyCustomerMessage(messageHtml) {
      if (!customerMessage || !messageHtml) return;
      customerMessage.innerHTML = messageHtml;
    }

    const replyHistoryKeyPrefix = 'avoidbit_order_tracker_reply_history_v1';

    function replyHistoryKey(orderNumber) {
      return `${replyHistoryKeyPrefix}:${normalizeOrderNumber(orderNumber || '')}`;
    }

    function readReplyHistory(orderNumber) {
      try {
        const raw = localStorage.getItem(replyHistoryKey(orderNumber));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }

    function writeReplyHistory(orderNumber, history) {
      try {
        localStorage.setItem(replyHistoryKey(orderNumber), JSON.stringify(Array.isArray(history) ? history.slice(-25) : []));
      } catch (error) {
        // Ignore storage failures; message delivery should still work.
      }
    }

    function parseOrderTrackerNoteEntry(noteText) {
      const raw = String(noteText || '').trim();
      if (!raw || !raw.startsWith(ORDER_TRACKER_NOTE_PREFIX)) return null;

      const jsonText = raw.slice(ORDER_TRACKER_NOTE_PREFIX.length).trim();
      if (!jsonText) return null;

      try {
        const parsed = JSON.parse(jsonText);
        if (!parsed || typeof parsed !== 'object') return null;
        return parsed;
      } catch (error) {
        return null;
      }
    }

    function createOrderTrackerNoteEntry(payload) {
      const record = {
        v: 1,
        type: 'order-tracker-reply',
        id: String(payload.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`),
        orderNumber: String(payload.orderNumber || ''),
        createdAt: String(payload.createdAt || new Date().toISOString()),
        subject: String(payload.subject || ''),
        message: String(payload.message || ''),
        attachmentName: String(payload.attachmentName || ''),
        attachmentSize: Number(payload.attachmentSize || 0),
        attachmentType: String(payload.attachmentType || ''),
        replyEmail: String(payload.replyEmail || ''),
        notifyByEmail: Boolean(payload.notifyByEmail),
        stage: Number(payload.stage || 0),
        printProgress: String(payload.printProgress || ''),
        itemCount: Number(payload.itemCount || 0),
        items: Array.isArray(payload.items) ? payload.items : [],
        authorRole: String(payload.authorRole || 'customer'),
        authorName: String(payload.authorName || 'Customer'),
      };

      return `${ORDER_TRACKER_NOTE_PREFIX}${JSON.stringify(record)}`;
    }

    function normalizeReplyHistoryEntries(entries, orderNumber) {
      if (!Array.isArray(entries)) return [];

      return entries
        .map((entry) => {
          let source = entry;

          if (typeof entry === 'string') {
            const parsedFromString = parseOrderTrackerNoteEntry(entry);
            if (!parsedFromString) return null;
            source = parsedFromString;
          }

          if (!source || typeof source !== 'object') return null;

          const maybeEmbeddedNote =
            source.note
            || source.comment
            || source.message
            || source.body
            || source.content
            || '';
          if (typeof maybeEmbeddedNote === 'string' && maybeEmbeddedNote.startsWith(ORDER_TRACKER_NOTE_PREFIX)) {
            const parsedEmbedded = parseOrderTrackerNoteEntry(maybeEmbeddedNote);
            if (parsedEmbedded) {
              source = { ...source, ...parsedEmbedded };
            }
          }

          const message = sanitizeReplyMessage(
            source.message
            || source.body
            || source.text
            || source.content
            || ''
          );
          if (!message) return null;

          const createdAt = String(
            source.createdAt
            || source.created_at
            || source.sentAt
            || source.sent_at
            || source.timestamp
            || new Date().toISOString()
          );

          const subject = String(
            source.subject
            || source.title
            || `Question about order #${orderNumber}`
          ).trim();

          return {
            id: String(source.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`),
            createdAt,
            subject,
            message,
            authorRole: String(source.authorRole || source.author_role || 'customer').trim().toLowerCase() === 'staff' ? 'staff' : 'customer',
            authorName: String(source.authorName || source.author_name || '').trim(),
            attachmentName: String(source.attachmentName || source.fileName || ''),
            attachmentSize: source.attachmentSize != null ? Number(source.attachmentSize) : 0,
            attachmentType: String(source.attachmentType || ''),
            attachmentDataUrl: String(source.attachmentDataUrl || ''),
            attachmentUrl: String(source.attachmentUrl || ''),
            orderNumber,
            replyEmail: String(source.replyEmail || source.email || ''),
            notifyByEmail: Boolean(source.notifyByEmail ?? source.notify_by_email ?? source.replyEmail ?? source.email),
          };
        })
        .filter(Boolean)
        .slice(-25);
    }

    function extractReplyHistoryFromPayload(payload, orderNumber) {
      if (!payload || typeof payload !== 'object') return [];

      const candidates = [
        payload.history,
        payload.messages,
        payload.replies,
        payload.notes,
        payload.comments,
        payload.items,
        payload.data?.history,
        payload.data?.messages,
        payload.data?.replies,
        payload.data?.notes,
        payload.data?.comments,
        payload.result?.history,
        payload.result?.messages,
        payload.result?.replies,
        payload.result?.notes,
        payload.result?.comments,
        payload.thread,
      ];

      for (let i = 0; i < candidates.length; i += 1) {
        const normalized = normalizeReplyHistoryEntries(candidates[i], orderNumber);
        if (normalized.length) return normalized;
      }

      return [];
    }

    function mergeReplyHistoryEntries(orderNumber, localEntries, remoteEntries) {
      const seen = new Set();
      const normalizedLocal = normalizeReplyHistoryEntries(localEntries, orderNumber);
      const normalizedRemote = normalizeReplyHistoryEntries(remoteEntries, orderNumber);

      return [...normalizedLocal, ...normalizedRemote]
        .filter((entry) => {
          const dedupeKey = `${String(entry.id || '')}|${String(entry.createdAt || '')}|${String(entry.message || '')}`;
          if (seen.has(dedupeKey)) return false;
          seen.add(dedupeKey);
          return true;
        })
        .slice(-25);
    }

    async function requestReplyHistoryByOrderNumber(session) {
      if (!replyHistoryEndpoint || !session || !session.orderNumber || !session.sessionToken) {
        return null;
      }

      try {
        const url = new URL(replyHistoryEndpoint, window.location.origin);
        url.searchParams.set('orderNumber', session.orderNumber);

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${session.sessionToken}`,
          },
        });

        const data = await parseResponseJSON(response);
        if (!response.ok || data.ok === false) {
          return null;
        }

        return extractReplyHistoryFromPayload(data, session.orderNumber);
      } catch (error) {
        return null;
      }
    }

    async function hydrateReplyHistory(orderNumber) {
      if (activeSession && normalizeOrderNumber(activeSession.orderNumber) === normalizeOrderNumber(orderNumber)) {
        const remoteHistory = await requestReplyHistoryByOrderNumber(activeSession);
        if (Array.isArray(remoteHistory)) {
          writeReplyHistory(orderNumber, remoteHistory);
        }
      }

      renderReplyHistory(orderNumber, { incremental: true });
    }

    function formatReplyTimestamp(timestamp) {
      try {
        return new Intl.DateTimeFormat(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }).format(new Date(timestamp));
      } catch (error) {
        return String(timestamp || '');
      }
    }

    function replyHistoryEntryKey(entry) {
      if (!entry || typeof entry !== 'object') return '';
      const id = String(entry.id || '').trim();
      if (id) return `id:${id}`;
      return `fallback:${String(entry.createdAt || '')}|${String(entry.message || '')}`;
    }

    function createReplyHistoryItem(entry) {
      const item = document.createElement('li');
      const role = String(entry.authorRole || '').trim().toLowerCase() === 'staff' ? 'staff' : 'customer';
      item.className = `order-tracker__reply-history-item is-${role}`;
      item.dataset.historyKey = replyHistoryEntryKey(entry);
      item.dataset.authorRole = role;

      const meta = document.createElement('p');
      meta.className = 'order-tracker__reply-history-meta';
      const leftMeta = document.createElement('span');
      const authorLabel = role === 'staff'
        ? (String(entry.authorName || '').trim() || 'Avoidbit Staff')
        : (String(entry.authorName || '').trim() || 'You');
      leftMeta.textContent = `${authorLabel} · ${formatReplyTimestamp(entry.createdAt)}`;
      const rightMeta = document.createElement('span');
      rightMeta.textContent = entry.subject || '';
      meta.appendChild(leftMeta);
      meta.appendChild(rightMeta);

      const body = document.createElement('p');
      body.className = 'order-tracker__reply-history-body';
      body.textContent = entry.message || '';

      item.appendChild(meta);
      item.appendChild(body);

      const attachmentLabel = entry.attachmentName
        ? `Attachment: ${entry.attachmentName}${Number.isFinite(entry.attachmentSize) && entry.attachmentSize > 0 ? ` (${Math.max(1, Math.ceil(entry.attachmentSize / 1024))} KB)` : ''}`
        : '';

      if (attachmentLabel) {
        const attachmentButton = document.createElement('button');
        attachmentButton.type = 'button';
        attachmentButton.className = 'order-tracker__reply-history-attachment';
        attachmentButton.textContent = entry.attachmentName || 'Open attachment';
        attachmentButton.addEventListener('click', () => openReplyAttachmentPreview(entry));
        item.appendChild(attachmentButton);
      }

      return item;
    }

    function renderReplyHistory(orderNumber, options = {}) {
      if (!replyHistorySection || !replyHistoryList) return;
      const incremental = Boolean(options && options.incremental);
      const wasNearBottom = (replyHistoryList.scrollHeight - replyHistoryList.clientHeight - replyHistoryList.scrollTop) <= 24;

      const history = readReplyHistory(orderNumber);
      if (replyHistoryEmpty) {
        replyHistoryEmpty.hidden = history.length > 0;
      }
      replyHistorySection.hidden = false;

      if (!history.length) {
        if (replyHistoryList.children.length) {
          replyHistoryList.innerHTML = '';
        }
        return;
      }

      const entriesToRender = history.slice();

      if (incremental && replyHistoryList.children.length) {
        const existingNodes = Array.from(replyHistoryList.querySelectorAll('[data-history-key]'));
        const existingKeys = existingNodes.map((node) => String(node.getAttribute('data-history-key') || ''));
        const nextKeys = entriesToRender.map((entry) => replyHistoryEntryKey(entry));

        if (nextKeys.length >= existingKeys.length) {
          const newCount = nextKeys.length - existingKeys.length;
          const prefixMatches = existingKeys.every((key, index) => key === nextKeys[index]);
          if (prefixMatches) {
            if (newCount > 0) {
              const newEntries = entriesToRender.slice(existingKeys.length);
              newEntries.forEach((entry) => {
                replyHistoryList.appendChild(createReplyHistoryItem(entry));
              });
            }
            if (wasNearBottom || newCount > 0) {
              requestAnimationFrame(() => {
                replyHistoryList.scrollTop = replyHistoryList.scrollHeight;
              });
            }
            return;
          }
        }
      }

      replyHistoryList.innerHTML = '';

      entriesToRender.forEach((entry) => {
        replyHistoryList.appendChild(createReplyHistoryItem(entry));
      });

      if (wasNearBottom || !incremental) {
        requestAnimationFrame(() => {
          replyHistoryList.scrollTop = replyHistoryList.scrollHeight;
        });
      }
    }

    function readFileAsDataUrl(file) {
      return new Promise((resolve, reject) => {
        if (!file) {
          resolve('');
          return;
        }

        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('Unable to read the selected file.'));
        reader.readAsDataURL(file);
      });
    }

    function getReplyAttachmentValidationError(file) {
      if (!file) return null;

      const fileName = String(file.name || '').trim();
      const normalizedName = fileName.toLowerCase();
      const allowedExtensions = ['.jpg', '.jpeg', '.png', '.svg', '.stl'];
      const allowedMimeTypes = [
        'image/jpeg',
        'image/png',
        'image/svg+xml',
        'model/stl',
        'application/sla',
        'application/octet-stream',
        'application/stl',
      ];
      const mimeType = String(file.type || '').trim().toLowerCase();
      const hasAllowedExtension = allowedExtensions.some((extension) => normalizedName.endsWith(extension));
      const hasAllowedMimeType = allowedMimeTypes.includes(mimeType);

      if (file.size > 5 * 1024 * 1024) {
        return 'File too large. Maximum file size is 5 MB.';
      }

      if (!hasAllowedExtension && !hasAllowedMimeType) {
        return 'Unsupported file type. Allowed: JPG, PNG, SVG, STL.';
      }

      return null;
    }

    function setReplyBusy(isBusy) {
      if (replySection) {
        replySection.classList.toggle('is-busy', isBusy);
      }
      if (replySubmit) {
        replySubmit.disabled = isBusy;
      }
      if (replyInput) {
        replyInput.disabled = isBusy;
      }
      if (replyFileInput) {
        replyFileInput.disabled = isBusy;
      }
    }

    function getReplyEmailNotificationEnabled() {
      return !!(replyEmailToggle ? replyEmailToggle.checked : true);
    }

    function closeReplyDialog() {
      if (replyDialog) {
        replyDialog.hidden = true;
      }
      if (replySection) {
        replySection.classList.remove('is-dialog-open');
      }
      setReplyBusy(false);
      if (replyStatus) {
        replyStatus.textContent = '';
      }
      if (replyInput) {
        replyInput.focus();
      }
    }

    function openReplyDialog() {
      if (replyDialogMessage) {
        replyDialogMessage.textContent = 'Your message has been sent.';
      }
      if (replyDialog) {
        replyDialog.hidden = false;
      }
      if (replySection) {
        replySection.classList.add('is-dialog-open');
      }
    }

    function resetReplyComposer() {
      if (replyInput) {
        replyInput.value = '';
      }
      if (replyChars) {
        replyChars.textContent = '0 / 1000';
      }
      if (replyStatus) {
        replyStatus.textContent = '';
      }
    }

    function finalizePendingReplyEntry() {
      if (!pendingReplyHistoryEntry || !activeSession) {
        closeReplyDialog();
        return;
      }

      const history = readReplyHistory(activeSession.orderNumber);
      writeReplyHistory(activeSession.orderNumber, [...history, pendingReplyHistoryEntry]);
      renderReplyHistory(activeSession.orderNumber);

      resetReplyComposer();

      pendingReplyHistoryEntry = null;
      closeReplyDialog();
    }

    function clearOrderPreview() {
      if (previewImage) {
        previewImage.src = '';
        previewImage.alt = '';
        previewImage.hidden = true;
      }
      if (previewPlaceholder) {
        previewPlaceholder.hidden = false;
      }
    }

    function syncSelectionState(orderIndex) {
      if (!orderItemsList) return;

      const listItems = orderItemsList.querySelectorAll('.order-tracker__order-item[data-order-index]');
      listItems.forEach((node) => {
        const nodeIndex = Number(node.dataset.orderIndex || -1);
        const isSelected = nodeIndex === orderIndex;
        node.classList.toggle('is-selected', isSelected);
        node.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      });

      const conveyorNodes = root.querySelectorAll('.order-tracker__belt-item[data-order-index]');
      conveyorNodes.forEach((node) => {
        const nodeIndex = Number(node.dataset.orderIndex || -1);
        const isSelected = nodeIndex === orderIndex;
        node.classList.toggle('is-selected', isSelected);
        node.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      });
    }

    async function renderSelectedPreview(item, orderNumber) {
      if (!previewImage || !previewPlaceholder) return;
      if (!item) {
        clearOrderPreview();
        return;
      }

      previewPlaceholder.hidden = true;
      previewImage.hidden = true;
      previewImage.src = '';
      previewImage.alt = '';

      try {
        const resolved = await resolveColorizerFromProductUrl(
          item.colorizerUrl || '',
          item.primaryColor,
          item.secondaryColor,
          {
            lockPrimary: item.hasExplicitPrimaryColor,
            lockSecondary: item.hasExplicitSecondaryColor,
          }
        );

        const resolvedItem = resolved ? { ...item, ...resolved } : item;

        let canvas = null;
        if (resolvedItem.baseSrc) {
          canvas = await renderColorizedCanvas(
            resolvedItem.baseSrc,
            resolvedItem.maskSrc || '',
            resolvedItem.primaryColor,
            resolvedItem.secondaryColor,
            '',
            ''
          );
        } else {
          const renderedImageUrl = await requestRenderedVariantImage(resolvedItem, orderNumber);
          if (renderedImageUrl) {
            canvas = await renderCutoutCanvas(renderedImageUrl);
          }
        }

        if (!canvas) {
          canvas = renderPlaceholderCanvas(resolvedItem.primaryColor, resolvedItem.secondaryColor, resolvedItem.title || 'Item');
        }

        previewImage.src = canvasToCroppedDataUrl(canvas);
        previewImage.alt = resolvedItem.title || 'Order item preview';
        previewImage.hidden = false;
      } catch (error) {
        const fallbackCanvas = renderPlaceholderCanvas(item.primaryColor, item.secondaryColor, item.title || 'Item');
        previewImage.src = canvasToCroppedDataUrl(fallbackCanvas);
        previewImage.alt = item.title || 'Order item preview';
        previewImage.hidden = false;
      }
    }

    function selectOrderItem(orderIndex, options = {}) {
      if (!Number.isInteger(orderIndex) || orderIndex < 0) return;

      selectedOrderIndex = orderIndex;
      syncSelectionState(orderIndex);

      const selectedItem = currentOrderItems[orderIndex];
      const { lane } = getConveyorRefs();
      if (lane) {
        const selectedPalette = selectedItem ? buildConveyorHexPalette([selectedItem]) : [];
        const fallbackPalette = selectedPalette.length ? selectedPalette : buildConveyorHexPalette(currentOrderItems);
        startConveyorHexAnimation(lane, fallbackPalette);
      }

      if (selectedItem) {
        if (options.scroll !== false) {
          scrollConveyorToOrderIndex(orderIndex);
        }
        void renderSelectedPreview(selectedItem, activeSession?.orderNumber || '');
      } else {
        clearOrderPreview();
      }
    }

    function getConveyorRefs() {
      return {
        lane: root.querySelector('[data-tracker-belt-lane]'),
        prev: root.querySelector('[data-tracker-belt-prev]'),
        next: root.querySelector('[data-tracker-belt-next]'),
      };
    }

    function updateConveyorNavState() {
      const { lane, prev, next } = getConveyorRefs();
      if (!lane || !prev || !next) return;

      const maxScroll = Math.max(0, lane.scrollWidth - lane.clientWidth);
      const canScroll = maxScroll > 6;

      prev.disabled = !canScroll || lane.scrollLeft <= 2;
      next.disabled = !canScroll || lane.scrollLeft >= maxScroll - 2;
    }

    function scrollConveyorBy(delta) {
      const { lane } = getConveyorRefs();
      if (!lane) return;

      lane.scrollBy({
        left: delta,
        behavior: 'smooth',
      });

      window.setTimeout(updateConveyorNavState, 220);
    }

    function scrollConveyorToOrderIndex(orderIndex, behavior = 'smooth') {
      const { lane } = getConveyorRefs();
      if (!lane) return;

      const target = lane.querySelector(`.order-tracker__belt-item[data-order-index="${orderIndex}"]`);
      if (!target) return;

      const targetOffset = target.offsetLeft - Math.max(0, (lane.clientWidth - target.clientWidth) / 2);
      const maxScroll = Math.max(0, lane.scrollWidth - lane.clientWidth);
      const left = Math.max(0, Math.min(targetOffset, maxScroll));

      lane.scrollTo({ left, behavior });
      window.setTimeout(updateConveyorNavState, 220);
    }

    function wireConveyorListSelectionSync() {
      if (!orderItemsList) return;

      const listItems = [...orderItemsList.querySelectorAll('.order-tracker__order-item[data-order-index]')];
      const beltItems = [...root.querySelectorAll('.order-tracker__belt-item[data-order-index]')];
      if (!listItems.length && !beltItems.length) return;

      listItems.forEach((node) => {
        node.onclick = () => {
          const orderIndex = Number(node.dataset.orderIndex || -1);
          if (orderIndex >= 0) {
            selectOrderItem(orderIndex);
          }
        };
        node.onkeydown = (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            const orderIndex = Number(node.dataset.orderIndex || -1);
            if (orderIndex >= 0) {
              selectOrderItem(orderIndex);
            }
          }
        };
      });

      beltItems.forEach((node) => {
        node.onclick = () => {
          const orderIndex = Number(node.dataset.orderIndex || -1);
          if (orderIndex >= 0) {
            selectOrderItem(orderIndex);
          }
        };
        node.onkeydown = (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            const orderIndex = Number(node.dataset.orderIndex || -1);
            if (orderIndex >= 0) {
              selectOrderItem(orderIndex);
            }
          }
        };
      });
    }

    function renderOrderItems(items) {
      if (!orderItemsList || !orderItemsCount || !orderItemsStatus) return;

      currentOrderItems = Array.isArray(items) ? items : [];
      selectedOrderIndex = -1;
      clearOrderPreview();
      orderItemsList.innerHTML = '';

      if (!currentOrderItems.length) {
        orderItemsCount.textContent = '';
        orderItemsStatus.hidden = false;
        orderItemsStatus.textContent = 'No order line items were returned by the order-items API for this order number.';
        return;
      }

      const totalQty = currentOrderItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
      orderItemsCount.textContent = `${totalQty} item${totalQty === 1 ? '' : 's'}`;
      orderItemsStatus.hidden = true;

      const frag = document.createDocumentFragment();
      currentOrderItems.forEach((item, index) => {
        const li = document.createElement('li');
        li.className = 'order-tracker__order-item';
        li.dataset.orderIndex = String(index);
        li.tabIndex = 0;
        li.setAttribute('role', 'button');
        li.setAttribute('aria-selected', 'false');

        const title = document.createElement('p');
        title.className = 'order-tracker__order-item-title';
        title.textContent = item.title;

        const meta = document.createElement('p');
        meta.className = 'order-tracker__order-item-meta';
        const metaParts = [`Qty: ${item.quantity}`];
        if (item.variantTitle) metaParts.push(item.variantTitle);
        if (item.sku) metaParts.push(`SKU: ${item.sku}`);
        meta.textContent = metaParts.join(' • ');

        const colors = document.createElement('p');
        colors.className = 'order-tracker__order-item-colors';
        colors.textContent = `Colors: ${item.primaryColor} / ${item.secondaryColor}`;

        li.appendChild(title);
        li.appendChild(meta);
        li.appendChild(colors);
        frag.appendChild(li);
      });

      orderItemsList.appendChild(frag);
      wireConveyorListSelectionSync();
      if (currentOrderItems.length) {
        selectOrderItem(0, { scroll: false });
      }
    }

    function expandItemsForConveyor(items) {
      const expanded = [];
      const MAX_CONVEYOR_ITEMS = 20;

      items.forEach((item, itemIndex) => {
        const count = Math.max(1, Math.min(item.quantity || 1, 6));
        for (let i = 0; i < count; i += 1) {
          expanded.push({ ...item, sourceOrderIndex: itemIndex });
          if (expanded.length >= MAX_CONVEYOR_ITEMS) return;
        }
      });

      return expanded;
    }

    function applyTrackerData(data, orderNumber) {
      const rawStage = Number(data.stage);
      const rawProgress = Number(data.printProgress);
      const stage = clamp(Number.isFinite(rawStage) ? rawStage : STAGE_MIN, STAGE_MIN, STAGE_MAX);
      const printProgress = clamp(Number.isFinite(rawProgress) ? rawProgress : 0, 0, 100);

      applyTrackerVisuals(stage, printProgress);
      applyCustomerMessage(data.customerMessage || '');

      if (orderDisplay) {
        orderDisplay.textContent = orderNumber;
      }
    }

    function stopLiveStatusPolling() {
      if (statusPollTimer) {
        window.clearInterval(statusPollTimer);
        statusPollTimer = null;
      }
      statusPollInFlight = false;
    }

    async function refreshLiveStatusOnce() {
      if (!activeSession || statusPollInFlight) return;
      if (String(activeSession.sessionToken || '').startsWith('internal-')) return;

      statusPollInFlight = true;
      try {
        const statusData = await requestStatusByOrderNumber(activeSession);
        applyTrackerData(
          {
            stage: Number(statusData.stage),
            printProgress: Number(statusData.printProgress),
            customerMessage: String(statusData.customerMessage || ''),
          },
          activeSession.orderNumber
        );
        await hydrateReplyHistory(activeSession.orderNumber);
      } catch (error) {
        // Keep polling quietly; intermittent network errors should not interrupt the session UI.
      } finally {
        statusPollInFlight = false;
      }
    }

    function startLiveStatusPolling() {
      stopLiveStatusPolling();
      if (!activeSession) return;
      if (String(activeSession.sessionToken || '').startsWith('internal-')) return;

      statusPollTimer = window.setInterval(() => {
        void refreshLiveStatusOnce();
      }, STATUS_POLL_INTERVAL_MS);
    }

    function startSession(session) {
      activeSession = session;
      writeStoredSession(session);
      setPanelVisibility(true);
      setGateStatus('');
      renderReplyHistory(session.orderNumber);
      void hydrateReplyHistory(session.orderNumber);
      startLiveStatusPolling();
      if (replyDialog) replyDialog.hidden = true;
      if (replySection) replySection.classList.remove('is-dialog-open');
      setReplyBusy(false);
    }

    function endSession() {
      stopLiveStatusPolling();
      activeSession = null;
      currentOrderItems = [];
      selectedOrderIndex = -1;
      clearStoredSession();
      clearOrderPreview();
      setPanelVisibility(false);
      setGateStatus('Session ended. Enter order number and PIN to track again.');
      form.reset();
      if (replyHistoryList) replyHistoryList.innerHTML = '';
      if (replyHistorySection) replyHistorySection.hidden = false;
      if (replyHistoryEmpty) replyHistoryEmpty.hidden = false;
      if (replyDialog) replyDialog.hidden = true;
      if (replySection) replySection.classList.remove('is-dialog-open', 'is-busy');
      setReplyBusy(false);
    }

    async function requestSession(orderNumber, contact, accessCode) {
      const normalizedContact = normalizeContact(contact);
      const normalizedAccessCode = String(accessCode || '').trim();

      if (authEndpoint) {
        const response = await fetch(new URL(authEndpoint, window.location.origin).toString(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            orderNumber,
            contact: normalizedContact,
            accessCode: normalizedAccessCode,
          }),
        });

        const data = await parseResponseJSON(response);
        if (!response.ok || data.ok === false) {
          throw new Error(data.message || 'Verification failed. Please check the access code and try again.');
        }

        if (!data.sessionToken) {
          throw new Error('Tracker auth response was missing a session token.');
        }

        return {
          ok: true,
          orderNumber: normalizeOrderNumber(data.orderNumber || orderNumber),
          sessionToken: String(data.sessionToken),
        };
      }

      if (normalizedAccessCode !== INTERNAL_ACCESS_CODE) {
        throw new Error('Enter the access code 1111 to continue.');
      }

      const internalRecord = getInternalOrderRecord(orderNumber);
      if (!internalRecord) {
        throw new Error('Could not verify order details. The order number was not found.');
      }

      const isMatch = internalRecord.contacts.some((candidate) => {
        return normalizeContact(candidate) === normalizedContact;
      });

      if (!isMatch) {
        throw new Error('Could not verify order details. The email or phone did not match this order.');
      }

      return {
        ok: true,
        orderNumber,
        sessionToken: `internal-${Date.now()}`,
      };
    }

    if (requestPinButton) {
      requestPinButton.addEventListener('click', async () => {
        const orderNumber = normalizeOrderNumber(orderInput.value);
        const contact = (contactInput.value || '').trim();

        if (!orderNumber || !contact) {
          setGateStatus('Enter your order number and email or phone before requesting a code.');
          return;
        }

        setSubmitting(true);
        setGateStatus('Sending your access code...');

        try {
          await requestAccessCode(orderNumber, contact);
          setGateStatus('Your access code has been sent. Check your inbox and then enter it here.');
        } catch (error) {
          const message = error instanceof Error ? error.message : 'We could not send an access code right now.';
          setGateStatus(message);
        } finally {
          setSubmitting(false);
        }
      });
    }

    async function requestOrderItemsByOrderNumber(session) {
      if (!orderItemsEndpoint) {
        throw new Error('Order-items endpoint is not configured.');
      }

      try {
        const url = new URL(orderItemsEndpoint, window.location.origin);
        url.searchParams.set('orderNumber', session.orderNumber);

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${session.sessionToken}`,
          },
        });

        const data = await parseResponseJSON(response);
        if (!response.ok || data.ok === false) {
          const message = data.message || `Order-items API returned ${response.status}.`;
          throw new Error(message);
        }

        const items = extractOrderItemsFromPayload(data);
        if (!Array.isArray(items) || !items.length) {
          throw new Error('Live order-items API returned no line items for this order.');
        }

        return items;
      } catch (error) {
        throw error instanceof Error ? error : new Error('Unable to load order items from the live API.');
      }
    }

    async function requestStatusByOrderNumber(session) {
      if (!statusEndpoint) {
        throw new Error('Status endpoint is not configured.');
      }

      try {
        const url = new URL(statusEndpoint, window.location.origin);
        url.searchParams.set('orderNumber', session.orderNumber);

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${session.sessionToken}`,
          },
        });

        const data = await parseResponseJSON(response);
        if (!response.ok || data.ok === false) {
          const message = data.message || `Status API returned ${response.status}.`;
          throw new Error(message);
        }

        return data;
      } catch (error) {
        throw error instanceof Error ? error : new Error('Unable to load order status from the live API.');
      }
    }

    async function requestTrackerData(session) {
      if (String(session.sessionToken || '').startsWith('internal-')) {
        const record = getInternalOrderRecord(session.orderNumber);
        if (!record) {
          throw new Error('Internal order record not found for this order number.');
        }

        return {
          stage: Number(record.stage),
          printProgress: Number(record.printProgress),
          customerMessage: String(record.customerMessage || ''),
          items: Array.isArray(record.items) ? record.items : [],
        };
      }

      const statusData = await requestStatusByOrderNumber(session);
      const apiItems = await requestOrderItemsByOrderNumber(session);

      return {
        stage: Number(statusData.stage),
        printProgress: Number(statusData.printProgress),
        customerMessage: String(statusData.customerMessage || ''),
        items: apiItems,
      };
    }

    function showLiveDataError(message) {
      if (orderItemsStatus) {
        orderItemsStatus.hidden = false;
        orderItemsStatus.textContent = message;
      }

      const conveyorStatusEl = root.querySelector('[data-tracker-conveyor-status]');
      if (conveyorStatusEl) {
        conveyorStatusEl.hidden = false;
        conveyorStatusEl.textContent = message;
      }
    }

    async function hydratePanel() {
      if (!activeSession) return;

      try {
        const data = await requestTrackerData(activeSession);
        applyTrackerData(data, activeSession.orderNumber);

        const normalizedItems = normalizeOrderItems(data.items || []);
        renderOrderItems(normalizedItems);
        await initConveyor(normalizedItems);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to load live tracker data right now.';
        showLiveDataError(message);
        setGateStatus(message);
      }
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const orderNumber = normalizeOrderNumber(orderInput.value);
      const contact = (contactInput.value || '').trim();
      const accessCode = (accessCodeInput.value || '').trim();

      if (!orderNumber || !contact || !accessCode) {
        setGateStatus('Enter your order number, email or phone, and access code.');
        return;
      }

      setSubmitting(true);
      setGateStatus('Verifying your secure tracker session...');

      try {
        const sessionResponse = await requestSession(orderNumber, contact, accessCode);

        const session = {
          orderNumber: sessionResponse.orderNumber,
          sessionToken: sessionResponse.sessionToken,
          contactEmail: extractEmailAddress(contact),
          createdAt: Date.now(),
        };

        startSession(session);
        await hydratePanel();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Verification failed. Please try again.';
        setGateStatus(message);
      } finally {
        setSubmitting(false);
      }
    });

    if (endSessionButton) {
      endSessionButton.addEventListener('click', endSession);
    }

    // ── Reply form ──────────────────────────────────────────────
    const replySection = root.querySelector('[data-tracker-reply-section]') || root.querySelector('.order-tracker__reply');
    const replyForm = root.querySelector('[data-tracker-reply-form]');
    const replyInput = root.querySelector('[data-tracker-reply-input]');
    const replySubmit = root.querySelector('[data-tracker-reply-submit]');
    const replyStatus = root.querySelector('[data-tracker-reply-status]');
    const replyChars = root.querySelector('[data-tracker-reply-chars]');
    const replyFileInput = root.querySelector('[data-tracker-reply-file]');
    const replyFileName = root.querySelector('[data-tracker-reply-file-name]');
    const replyEmailToggle = root.querySelector('[data-tracker-reply-email-toggle]');
    let replyHistorySection = root.querySelector('[data-tracker-reply-history]');
    let replyHistoryList = root.querySelector('[data-tracker-reply-history-list]');
    let replyHistoryEmpty = root.querySelector('[data-tracker-reply-history-empty]');
    let replyDialog = root.querySelector('[data-tracker-reply-dialog]');
    let replyDialogOk = root.querySelector('[data-tracker-reply-dialog-ok]');
    let replyDialogMessage = root.querySelector('[data-tracker-reply-dialog-message]');
    let replyPreviewModal = root.querySelector('[data-tracker-reply-preview]');
    let replyPreviewClose = root.querySelector('[data-tracker-reply-preview-close]');
    let replyPreviewTitle = root.querySelector('[data-tracker-reply-preview-title]');
    let replyPreviewMedia = root.querySelector('[data-tracker-reply-preview-media]');
    const replyIframeName = `OrderTrackerReplyTarget-${root.id || 'default'}`;

    function ensureReplyField(name, options = {}) {
      if (!replyForm) return null;

      const fieldName = String(name || '').trim();
      if (!fieldName) return null;

      const selector = `[name="${selectorEscape(fieldName)}"]`;
      let field = replyForm.querySelector(selector);
      if (field) return field;

      const asTextarea = options.asTextarea === true;
      field = document.createElement(asTextarea ? 'textarea' : 'input');
      field.name = fieldName;

      if (asTextarea) {
        field.hidden = true;
      } else {
        field.type = options.type || 'hidden';
      }

      replyForm.appendChild(field);
      return field;
    }

    const replyFormTypeEl = ensureReplyField('form_type');
    const replyUtf8El = ensureReplyField('utf8');
    const replyContactNameEl = ensureReplyField('contact[name]');
    const replyContactEmailEl = ensureReplyField('contact[email]');
    const replyContactSubjectEl = ensureReplyField('contact[subject]');
    const replyContactTagsEl = ensureReplyField('contact[tags]');
    const replyContactBodyEl = ensureReplyField('contact[body]', { asTextarea: true });
    const replyReturnToEl = ensureReplyField('return_to');
    const replyCaptchaTokenEl = ensureReplyField('contact[captcha_token]');

    if (replySection && !replyHistorySection) {
      replyHistorySection = document.createElement('div');
      replyHistorySection.className = 'order-tracker__reply-history';
      replyHistorySection.dataset.trackerReplyHistory = 'true';

      const historyHeading = document.createElement('h4');
      historyHeading.textContent = 'Conversation history';

      replyHistoryList = document.createElement('ol');
      replyHistoryList.className = 'order-tracker__reply-history-list';
      replyHistoryList.dataset.trackerReplyHistoryList = 'true';

      replyHistoryEmpty = document.createElement('p');
      replyHistoryEmpty.className = 'order-tracker__reply-history-empty';
      replyHistoryEmpty.dataset.trackerReplyHistoryEmpty = 'true';
      replyHistoryEmpty.textContent = 'No messages yet. Your sent messages will appear here.';

      replyHistorySection.appendChild(historyHeading);
      replyHistorySection.appendChild(replyHistoryList);
      replyHistorySection.appendChild(replyHistoryEmpty);
      replyForm?.before(replyHistorySection);
    }

    if (replySection && !replyDialog) {
      replyDialog = document.createElement('div');
      replyDialog.className = 'order-tracker__reply-dialog';
      replyDialog.dataset.trackerReplyDialog = 'true';
      replyDialog.hidden = true;

      const dialogCard = document.createElement('div');
      dialogCard.className = 'order-tracker__reply-dialog-card';
      dialogCard.setAttribute('role', 'dialog');
      dialogCard.setAttribute('aria-modal', 'true');
      dialogCard.setAttribute('aria-labelledby', `OrderTrackerReplyDialogTitle-${root.id || 'default'}`);

      const dialogMessage = document.createElement('p');
      dialogMessage.id = `OrderTrackerReplyDialogTitle-${root.id || 'default'}`;
      dialogMessage.dataset.trackerReplyDialogMessage = 'true';
      dialogMessage.textContent = 'Your message has been sent.';
      replyDialogMessage = dialogMessage;

      replyDialogOk = document.createElement('button');
      replyDialogOk.type = 'button';
      replyDialogOk.className = 'button button--primary';
      replyDialogOk.dataset.trackerReplyDialogOk = 'true';
      replyDialogOk.textContent = 'OK';

      dialogCard.appendChild(dialogMessage);
      dialogCard.appendChild(replyDialogOk);
      replyDialog.appendChild(dialogCard);
      replySection.appendChild(replyDialog);
    }

    let replyTargetIframe = root.querySelector('[data-tracker-reply-iframe]');
    if (!replyTargetIframe && replySection) {
      replyTargetIframe = document.createElement('iframe');
      replyTargetIframe.hidden = true;
      replyTargetIframe.setAttribute('aria-hidden', 'true');
      replyTargetIframe.setAttribute('tabindex', '-1');
      replyTargetIframe.name = replyIframeName;
      replyTargetIframe.dataset.trackerReplyIframe = 'true';
      replySection.appendChild(replyTargetIframe);
    }

    let pendingReplyHistoryEntry = null;

    if (replyForm) {
      if (replyFormTypeEl) replyFormTypeEl.value = 'contact';
      if (replyUtf8El) replyUtf8El.value = '✓';
      if (replyContactNameEl) replyContactNameEl.value = 'Order Tracker Customer';
      if (replyContactTagsEl) replyContactTagsEl.value = 'order-tracker';
      if (replyCaptchaTokenEl) replyCaptchaTokenEl.value = '';

      if (replyInput && !replyInput.getAttribute('name')) {
        replyInput.setAttribute('name', 'message');
      }
    }

    if (replyDialogOk) {
      replyDialogOk.addEventListener('click', finalizePendingReplyEntry);
    }

    if (replyDialog) {
      replyDialog.hidden = true;
    }

    if (replySection && !replyPreviewModal) {
      replyPreviewModal = document.createElement('div');
      replyPreviewModal.className = 'order-tracker__reply-preview';
      replyPreviewModal.dataset.trackerReplyPreview = 'true';
      replyPreviewModal.hidden = true;

      const previewCard = document.createElement('div');
      previewCard.className = 'order-tracker__reply-preview-card';

      const previewHeader = document.createElement('div');
      previewHeader.className = 'order-tracker__reply-preview-header';

      replyPreviewTitle = document.createElement('p');
      replyPreviewTitle.className = 'order-tracker__reply-preview-title';
      replyPreviewTitle.dataset.trackerReplyPreviewTitle = 'true';
      replyPreviewTitle.textContent = 'Attachment preview';

      replyPreviewClose = document.createElement('button');
      replyPreviewClose.type = 'button';
      replyPreviewClose.className = 'order-tracker__reply-preview-close';
      replyPreviewClose.dataset.trackerReplyPreviewClose = 'true';
      replyPreviewClose.setAttribute('aria-label', 'Close preview');
      replyPreviewClose.textContent = 'Close';

      replyPreviewMedia = document.createElement('div');
      replyPreviewMedia.className = 'order-tracker__reply-preview-media';
      replyPreviewMedia.dataset.trackerReplyPreviewMedia = 'true';

      previewHeader.appendChild(replyPreviewTitle);
      previewHeader.appendChild(replyPreviewClose);
      previewCard.appendChild(previewHeader);
      previewCard.appendChild(replyPreviewMedia);
      replyPreviewModal.appendChild(previewCard);
      replySection.appendChild(replyPreviewModal);
    }

    function resetReplyPreviewModal() {
      if (replyPreviewMedia?.dataset.objectUrl) {
        URL.revokeObjectURL(replyPreviewMedia.dataset.objectUrl);
        delete replyPreviewMedia.dataset.objectUrl;
      }
      if (!replyPreviewMedia) return;
      replyPreviewMedia.innerHTML = '';
      if (replyPreviewTitle) {
        replyPreviewTitle.textContent = 'Attachment preview';
      }
    }

    function escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    async function loadReplyAttachmentObjectUrl(entry) {
      const attachmentUrl = String(entry?.attachmentUrl || '').trim();
      if (!attachmentUrl || !activeSession?.sessionToken) {
        return '';
      }

      const response = await fetch(new URL(attachmentUrl, window.location.origin).toString(), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${activeSession.sessionToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Unable to load the stored attachment.');
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    }

    function appendReplyAttachmentDownload(entry, objectUrl) {
      if (!replyPreviewMedia) return;

      const downloadButton = document.createElement('a');
      downloadButton.className = 'button button--secondary';
      downloadButton.textContent = 'Download attachment';
      downloadButton.href = objectUrl;
      downloadButton.download = String(entry?.attachmentName || 'attachment');
      downloadButton.rel = 'noopener';
      replyPreviewMedia.appendChild(downloadButton);
    }

    async function openReplyAttachmentPreview(entry) {
      if (!replyPreviewModal || !replyPreviewMedia || !replyPreviewTitle) return;

      resetReplyPreviewModal();
      const fileName = String(entry?.attachmentName || 'Attachment');
      const lowerFileName = fileName.toLowerCase();
      const mimeType = String(entry?.attachmentType || '').trim().toLowerCase();
      let dataUrl = String(entry?.attachmentDataUrl || '').trim();
      const attachmentUrl = String(entry?.attachmentUrl || '').trim();

      if (!dataUrl && attachmentUrl) {
        try {
          dataUrl = await loadReplyAttachmentObjectUrl(entry);
          if (dataUrl) {
            replyPreviewMedia.dataset.objectUrl = dataUrl;
          }
        } catch (error) {
          replyPreviewTitle.textContent = fileName;
          replyPreviewModal.hidden = false;
          const info = document.createElement('div');
          info.className = 'order-tracker__reply-preview-empty';
          info.textContent = error instanceof Error ? error.message : 'Unable to load the stored attachment.';
          replyPreviewMedia.appendChild(info);
          return;
        }
      }

      const isImageMime = mimeType.startsWith('image/') || mimeType === 'image/svg+xml';
      const isKnownImageExtension = lowerFileName.endsWith('.png') || lowerFileName.endsWith('.jpg') || lowerFileName.endsWith('.jpeg') || lowerFileName.endsWith('.svg');
      const hasRenderableDataUrl = dataUrl.startsWith('data:image/') || dataUrl.startsWith('blob:');
      const isRenderableInline = isImageMime || isKnownImageExtension || hasRenderableDataUrl;

      replyPreviewTitle.textContent = fileName;
      replyPreviewModal.hidden = false;

      if (isRenderableInline && (!dataUrl || hasRenderableDataUrl || isKnownImageExtension)) {
        const previewImage = document.createElement('img');
        previewImage.src = dataUrl || `data:image/svg+xml;charset=utf-8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"><rect width="120" height="120" fill="#0d1728"/><text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" fill="#dfefff" font-size="12">${escapeHtml(fileName)}</text></svg>')}`;
        previewImage.alt = fileName;
        replyPreviewMedia.appendChild(previewImage);
        if (dataUrl && dataUrl.startsWith('blob:')) {
          appendReplyAttachmentDownload(entry, dataUrl);
        }
        return;
      }

      if (lowerFileName.endsWith('.stl')) {
        const info = document.createElement('div');
        info.className = 'order-tracker__reply-preview-empty';
        info.textContent = 'STL preview is not rendered inline in-browser. Download the stored file below.';
        replyPreviewMedia.appendChild(info);
        if (dataUrl) {
          appendReplyAttachmentDownload(entry, dataUrl);
        }
        return;
      }

      const info = document.createElement('div');
      info.className = 'order-tracker__reply-preview-empty';
      info.textContent = 'This attachment cannot be previewed inline. Download the stored file below.';
      replyPreviewMedia.appendChild(info);
      if (dataUrl) {
        appendReplyAttachmentDownload(entry, dataUrl);
      }
    }

    if (replyPreviewClose) {
      replyPreviewClose.addEventListener('click', () => {
        if (replyPreviewModal) {
          replyPreviewModal.hidden = true;
          resetReplyPreviewModal();
        }
      });
    }

    if (replyPreviewModal) {
      replyPreviewModal.addEventListener('click', (event) => {
        if (event.target === replyPreviewModal) {
          replyPreviewModal.hidden = true;
          resetReplyPreviewModal();
        }
      });
    }

    if (replyInput && replyChars) {
      replyInput.addEventListener('input', () => {
        replyChars.textContent = `${replyInput.value.length} / 1000`;
      });
    }

    if (replyFileInput) {
      replyFileInput.addEventListener('change', () => {
        const selectedFile = replyFileInput.files && replyFileInput.files[0] ? replyFileInput.files[0] : null;
        const validationError = getReplyAttachmentValidationError(selectedFile);

        if (validationError) {
          if (replyStatus) replyStatus.textContent = validationError;
          replyFileInput.value = '';
          if (replyFileName) replyFileName.textContent = 'No file selected';
          return;
        }

        if (replyStatus) replyStatus.textContent = '';
        if (replyFileName) {
          replyFileName.textContent = selectedFile ? selectedFile.name : 'No file selected';
        }
      });
    }

    if (replyForm) {
      replyForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!activeSession) {
          if (replyStatus) replyStatus.textContent = 'Open your tracker session before sending a message.';
          return;
        }

        if (String(activeSession.sessionToken || '').startsWith('internal-')) {
          clearStoredSession();
          endSession();
          if (replyStatus) {
            replyStatus.textContent = 'Your previous tracker session was local fallback only. Request a new PIN and sign in again to send messages to order history.';
          }
          return;
        }

        const message = sanitizeReplyMessage(replyInput?.value || '');
        if (!message) {
          if (replyStatus) replyStatus.textContent = 'Please write a message before sending.';
          return;
        }

        const selectedFile = replyFileInput && replyFileInput.files && replyFileInput.files[0] ? replyFileInput.files[0] : null;
        const attachmentError = getReplyAttachmentValidationError(selectedFile);
        if (attachmentError) {
          if (replyStatus) replyStatus.textContent = attachmentError;
          return;
        }

        let attachmentDataUrl = '';
        let attachmentType = '';
        if (selectedFile) {
          try {
            attachmentDataUrl = await readFileAsDataUrl(selectedFile);
            attachmentType = String(selectedFile.type || '').trim();
          } catch (error) {
            if (replyStatus) replyStatus.textContent = 'Unable to read the selected file for preview.';
            return;
          }
        }

        const notifyByEmail = getReplyEmailNotificationEnabled();
        const replyEmail = notifyByEmail
          ? extractEmailAddress(activeSession.contactEmail || contactInput?.value || '')
          : '';

        if (notifyByEmail && !replyEmail) {
          if (replyStatus) replyStatus.textContent = 'Please use a valid email address in the tracker form so we can reply.';
          return;
        }

        const subject = `Question about order #${activeSession.orderNumber}`;
        const stage = clamp(Number(stageVisualEl?.dataset.stage || STAGE_MIN), STAGE_MIN, STAGE_MAX);
        const printProgressLabel = printPctEl?.textContent?.trim() || 'N/A';
        const itemCount = currentOrderItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const firstItemTitles = currentOrderItems
          .slice(0, 5)
          .map((item) => item.title)
          .filter(Boolean)
          .join(', ');
        const sentAt = new Date().toISOString();
        const attachmentSummary = selectedFile
          ? `Attachment: ${selectedFile.name}${selectedFile.size > 0 ? ` (${Math.max(1, Math.ceil(selectedFile.size / 1024))} KB)` : ''}`
          : '';
        const composedBody = [
          subject,
          '',
          message,
          ...(attachmentSummary ? ['', attachmentSummary] : []),
          '',
          '--- Order Context ---',
          `Order #: ${activeSession.orderNumber}`,
          ...(notifyByEmail ? [`Reply Email: ${replyEmail}`] : []),
          `Email Notification: ${notifyByEmail ? 'Enabled' : 'Disabled'}`,
          `Stage: ${stage}`,
          `Print Progress: ${printProgressLabel}`,
          `Item Count: ${itemCount}`,
          `Items: ${firstItemTitles || 'N/A'}`,
          `Sent At: ${sentAt}`,
        ].join('\n');

        const historyEntry = {
          id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          createdAt: sentAt,
          subject,
          message,
          attachmentName: selectedFile ? selectedFile.name : '',
          attachmentSize: selectedFile ? selectedFile.size : 0,
          attachmentType,
          attachmentDataUrl,
          orderNumber: activeSession.orderNumber,
          replyEmail: notifyByEmail ? replyEmail : '',
          notifyByEmail,
        };

        const trackerNotePayload = {
          ...historyEntry,
          createdAt: sentAt,
          stage,
          printProgress: printProgressLabel,
          itemCount,
          items: currentOrderItems,
        };
        const trackerNoteBody = createOrderTrackerNoteEntry(trackerNotePayload);
        const remoteEntryPayload = { ...trackerNotePayload };

        if (!replyFormTypeEl || !replyUtf8El || !replyContactNameEl || !replyContactEmailEl || !replyContactSubjectEl || !replyContactTagsEl || !replyContactBodyEl || !replyReturnToEl) {
          if (replyStatus) replyStatus.textContent = 'Message form could not be prepared for sending.';
          return;
        }

        replyFormTypeEl.value = 'contact';
        replyUtf8El.value = '✓';
        replyContactNameEl.value = 'Order Tracker Customer';
        replyContactEmailEl.value = notifyByEmail ? replyEmail : '';
        replyContactSubjectEl.value = subject;
        replyContactTagsEl.value = 'order-tracker';
        replyContactBodyEl.value = composedBody;
        replyReturnToEl.value = window.location.pathname;

        setReplyBusy(true);
        if (replyStatus) replyStatus.textContent = 'Sending message...';

        let persistedRemotely = false;
        if (replySendEndpoint) {
          try {
            const response = await fetch(new URL(replySendEndpoint, window.location.origin).toString(), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: `Bearer ${activeSession.sessionToken}`,
              },
              body: JSON.stringify({
                orderNumber: activeSession.orderNumber,
                subject,
                message,
                replyEmail,
                notifyByEmail,
                note: trackerNoteBody,
                entry: remoteEntryPayload,
              }),
            });

            const data = await parseResponseJSON(response);
            if (!response.ok || data.ok === false) {
              const apiMessage = data.message || 'Message could not be saved to order history.';
              throw new Error(apiMessage);
            }

            persistedRemotely = true;
          } catch (error) {
            if (replyStatus) {
              replyStatus.textContent = error instanceof Error
                ? error.message
                : 'Message could not be sent right now. Please try again.';
            }
            setReplyBusy(false);
            return;
          }
        }

        // Preserve current behavior when no remote endpoint is configured.
        if (persistedRemotely || !replySendEndpoint) {
          const existingHistory = readReplyHistory(activeSession.orderNumber);
          writeReplyHistory(activeSession.orderNumber, [...existingHistory, historyEntry]);
          pendingReplyHistoryEntry = null;
          renderReplyHistory(activeSession.orderNumber);
        }

        if (replyFileInput) {
          replyFileInput.value = '';
        }
        if (replyFileName) {
          replyFileName.textContent = 'No file selected';
        }
        if (replyHistorySection) replyHistorySection.hidden = false;
        if (replyStatus) replyStatus.textContent = '';
        setReplyBusy(false);
        if (notifyByEmail) {
          openReplyDialog();
        } else {
          resetReplyComposer();
        }

        if (persistedRemotely) {
          void hydrateReplyHistory(activeSession.orderNumber);
        }
      });
    }

    // ── Conveyor belt ───────────────────────────────────────────
    let conveyorReady = false;
    const variantRenderCache = new Map();
    const conveyorHexTimeoutIds = [];

    function buildConveyorHexLayer(tiles) {
      const topY = [2, 10, 26, 34];
      const bottomY = [36, 44, 60, 68];
      const hex = (cx, ySet, fill) => {
        const y0 = ySet[0];
        const y1 = ySet[1];
        const y2 = ySet[2];
        const y3 = ySet[3];

        const basePoints = `${cx},${y0} ${cx + 14},${y1} ${cx + 14},${y2} ${cx},${y3} ${cx - 14},${y2} ${cx - 14},${y1}`;
        const innerHex = `${cx},${y0 + 4} ${cx + 10},${y1 + 2} ${cx + 10},${y2 - 2} ${cx},${y3 - 4} ${cx - 10},${y2 - 2} ${cx - 10},${y1 + 2}`;
        const innerTopBand = `${cx - 10},${y1 + 2} ${cx},${y0 + 4} ${cx + 10},${y1 + 2} ${cx + 8},${y1 + 7} ${cx},${y1 + 9} ${cx - 8},${y1 + 7}`;
        const innerLeftBand = `${cx - 10},${y1 + 2} ${cx - 10},${y2 - 2} ${cx - 6},${y2 - 4} ${cx - 6},${y1 + 4}`;
        const innerBottomBand = `${cx - 10},${y2 - 2} ${cx},${y3 - 4} ${cx + 10},${y2 - 2} ${cx + 7},${y2 - 7} ${cx},${y2 - 9} ${cx - 7},${y2 - 7}`;
        const innerRightBand = `${cx + 10},${y1 + 2} ${cx + 10},${y2 - 2} ${cx + 6},${y2 - 4} ${cx + 6},${y1 + 4}`;
        const lowerRim = `${cx - 10},${y2 - 1} ${cx},${y3 - 3} ${cx + 10},${y2 - 1}`;

        return `
          <polygon points="${basePoints}" fill="${fill}" fill-opacity="0.86"/>
          <polygon points="${basePoints}" fill="#050b14" fill-opacity="0.34"/>
          <polygon points="${innerHex}" fill="#01040a" fill-opacity="0.52"/>
          <polygon points="${innerTopBand}" fill="#000000" fill-opacity="0.38"/>
          <polygon points="${innerLeftBand}" fill="#000000" fill-opacity="0.32"/>
          <polygon points="${innerBottomBand}" fill="#d8ecff" fill-opacity="0.08"/>
          <polygon points="${innerRightBand}" fill="#d8ecff" fill-opacity="0.07"/>
          <polyline points="${lowerRim}" fill="none" stroke="#f1f8ff" stroke-opacity="0.12" stroke-width="0.8" stroke-linecap="round"/>
          <polyline points="${basePoints} ${cx},${y0}" fill="none" stroke="#01040b" stroke-opacity="0.74" stroke-width="1.2"/>
        `;
      };

      const pieces = [
        hex(0, topY, tiles[0]),
        hex(40, topY, tiles[1]),
        hex(80, topY, tiles[2]),
        hex(120, topY, tiles[3]),
        hex(160, topY, tiles[4]),
        hex(200, topY, tiles[5]),
        // Seam match tile on the right edge should mirror left edge color
        hex(240, topY, tiles[0]),
        hex(20, bottomY, tiles[6]),
        hex(60, bottomY, tiles[7]),
        hex(100, bottomY, tiles[8]),
        hex(140, bottomY, tiles[9]),
        hex(180, bottomY, tiles[10]),
        hex(220, bottomY, tiles[11]),
      ].join('');

      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="68" viewBox="0 0 240 68" shape-rendering="crispEdges"><g opacity="0.72">${pieces}</g></svg>`;
      return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
    }

    function startConveyorHexAnimation(beltLaneEl, dynamicPalette = []) {
      if (!beltLaneEl) return;

      for (let i = 0; i < conveyorHexTimeoutIds.length; i += 1) {
        if (conveyorHexTimeoutIds[i]) {
          window.clearTimeout(conveyorHexTimeoutIds[i]);
          conveyorHexTimeoutIds[i] = null;
        }
      }

      const palette = Array.isArray(dynamicPalette) && dynamicPalette.length
        ? dynamicPalette
        : [];
      if (!palette.length) {
        beltLaneEl.style.setProperty('--ot-conveyor-hex-layer', 'none');
        return;
      }
      const tiles = new Array(12).fill(0).map((_, i) => palette[i % palette.length]);
      const lastChangedAt = new Array(tiles.length).fill(0);

      const applyLayer = () => {
        beltLaneEl.style.setProperty('--ot-conveyor-hex-layer', buildConveyorHexLayer(tiles));
      };

      applyLayer();

      const isAdjacentHex = (a, b) => {
        const top = [0, 1, 2, 3, 4, 5];
        const bottom = [6, 7, 8, 9, 10, 11];
        const topPos = top.indexOf(a);
        const bottomPos = bottom.indexOf(a);
        const bTopPos = top.indexOf(b);
        const bBottomPos = bottom.indexOf(b);

        if (topPos !== -1 && bTopPos !== -1) return Math.abs(topPos - bTopPos) === 1;
        if (bottomPos !== -1 && bBottomPos !== -1) return Math.abs(bottomPos - bBottomPos) === 1;
        if (topPos !== -1 && bBottomPos !== -1) return bBottomPos === topPos || bBottomPos === topPos - 1;
        if (bottomPos !== -1 && bTopPos !== -1) return bTopPos === bottomPos || bTopPos === bottomPos + 1;
        return false;
      };

      const pickNextColor = (current) => {
        let next = current;
        let safety = 0;
        while (next === current && safety < 10) {
          next = palette[Math.floor(Math.random() * palette.length)];
          safety += 1;
        }
        return next;
      };

      const schedulePulse = () => {
        const nextPulseMs = 230 + Math.floor(Math.random() * 420);
        conveyorHexTimeoutIds[0] = window.setTimeout(() => {
          const now = Date.now();
          const shuffled = [...tiles.keys()].sort(() => Math.random() - 0.5);
          const chosen = [];
          const changesTarget = Math.random() < 0.28 ? 2 : 1;

          for (let i = 0; i < shuffled.length && chosen.length < changesTarget; i += 1) {
            const idx = shuffled[i];
            if (now - lastChangedAt[idx] < 700) continue;

            let touchesNeighbor = false;
            for (let j = 0; j < chosen.length; j += 1) {
              if (isAdjacentHex(idx, chosen[j])) {
                touchesNeighbor = true;
                break;
              }
            }
            if (touchesNeighbor) continue;

            chosen.push(idx);
          }

          for (let i = 0; i < chosen.length; i += 1) {
            const tileIndex = chosen[i];
            const staggerMs = 60 + Math.floor(Math.random() * 340);
            const slot = tileIndex + 1;
            if (conveyorHexTimeoutIds[slot]) {
              window.clearTimeout(conveyorHexTimeoutIds[slot]);
            }
            conveyorHexTimeoutIds[slot] = window.setTimeout(() => {
              tiles[tileIndex] = pickNextColor(tiles[tileIndex]);
              lastChangedAt[tileIndex] = Date.now();
              applyLayer();
            }, staggerMs);
          }

          schedulePulse();
        }, nextPulseMs);
      };

      schedulePulse();
    }

    function buildConveyorHexPalette(orderItems) {
      const paletteSet = new Set();

      const toHex6 = (value) => {
        const rgb = cssColorToRgb(value);
        if (!rgb) return '';
        const toByteHex = (n) => Math.max(0, Math.min(255, Number(n) || 0)).toString(16).padStart(2, '0');
        return `#${toByteHex(rgb[0])}${toByteHex(rgb[1])}${toByteHex(rgb[2])}`;
      };

      for (let i = 0; i < (orderItems || []).length; i += 1) {
        const item = orderItems[i] || {};
        const colors = [
          item.primaryColor,
          item.secondaryColor,
          item.backgroundColor,
        ];

        for (let j = 0; j < colors.length; j += 1) {
          const hex = toHex6(colors[j]);
          if (hex) paletteSet.add(hex);
        }
      }

      const dynamicPalette = [...paletteSet];
      return dynamicPalette;
    }

    async function requestRenderedVariantImage(item, orderNumber) {
      if (!variantRenderEndpoint) return '';
      const variantId = String(item.variantId || parseVariantId(item.colorizerUrl || '') || '').trim();
      if (!variantId) return '';

      const key = `${variantId}|${item.primaryColor || ''}|${item.secondaryColor || ''}`;
      if (variantRenderCache.has(key)) {
        return variantRenderCache.get(key);
      }

      try {
        const response = await fetch(variantRenderEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${activeSession?.sessionToken || ''}`,
          },
          body: JSON.stringify({
            orderNumber,
            variantId,
            primaryColor: item.primaryColor,
            secondaryColor: item.secondaryColor,
            baseSrc: item.baseSrc || '',
            maskSrc: item.maskSrc || '',
            backgroundSrc: item.backgroundSrc || '',
            backgroundColor: item.backgroundColor || '',
            sku: item.sku || '',
            title: item.title || '',
            colorizerUrl: item.colorizerUrl || '',
          }),
        });

        const data = await parseResponseJSON(response);
        if (!response.ok || data.ok === false) {
          return '';
        }

        const debugSource = String(data.source || data.renderSource || '').trim();
        if (debugSource && debugSource !== 'variant-render') {
          // keep returning the image if present, but this helps future debugging via the backend
        }

        const url = String(
          data.renderedImageUrl
          || data.imageUrl
          || data.cutoutUrl
          || data.url
          || ''
        ).trim();

        if (!url) return '';

        variantRenderCache.set(key, url);
        return url;
      } catch (error) {
        return '';
      }
    }

    async function initConveyor(orderItems) {
      if (conveyorReady) return;

      const conveyorEl = root.querySelector('[data-tracker-conveyor]');
      if (!conveyorEl) return;

      const beltItemsEl = conveyorEl.querySelector('[data-tracker-belt-items]');
      const beltLaneEl = conveyorEl.querySelector('[data-tracker-belt-lane]');
      const beltPrevEl = conveyorEl.querySelector('[data-tracker-belt-prev]');
      const beltNextEl = conveyorEl.querySelector('[data-tracker-belt-next]');
      const conveyorStatusEl = conveyorEl.querySelector('[data-tracker-conveyor-status]');
      if (!beltItemsEl) return;

      conveyorReady = true;

      const itemData = Array.isArray(orderItems) && orderItems.length
        ? expandItemsForConveyor(orderItems)
        : [];

      if (!itemData.length) {
        if (conveyorStatusEl) {
          conveyorStatusEl.hidden = false;
          conveyorStatusEl.textContent = 'No live order items are available to render in the conveyor.';
        }
        return;
      }

      if (conveyorStatusEl) conveyorStatusEl.textContent = 'Rendering your items\u2026';

      // If item has a colorizer URL (product page with variant query), resolve base/mask/colors
      // from that page's colorizer metadata so the belt reflects real customer selections.
      const resolvedItems = await Promise.all(
        itemData.map(async (item) => {
          const resolved = await resolveColorizerFromProductUrl(
            item.colorizerUrl || '',
            item.primaryColor,
            item.secondaryColor,
            {
              lockPrimary: item.hasExplicitPrimaryColor,
              lockSecondary: item.hasExplicitSecondaryColor,
            }
          );

          if (!resolved) return item;

          return {
            ...item,
            baseSrc: resolved.baseSrc || item.baseSrc || '',
            maskSrc: resolved.maskSrc || item.maskSrc || '',
            primaryColor: resolved.primaryColor || item.primaryColor || '#594abd',
            secondaryColor: resolved.secondaryColor || item.secondaryColor || '#291e62',
            backgroundSrc: resolved.backgroundSrc || item.backgroundSrc || '',
            backgroundColor: resolved.backgroundColor || item.backgroundColor || '',
          };
        })
      );

      let usedLocalRenderer = false;

      const canvases = await Promise.all(
        resolvedItems.map(async (item) => {
          try {
            if (item.baseSrc) {
              usedLocalRenderer = true;
              return await renderColorizedCanvas(
                item.baseSrc,
                item.maskSrc || '',
                item.primaryColor,
                item.secondaryColor,
                '',
                ''
              );
            }

            const renderedImageUrl = await requestRenderedVariantImage(item, activeSession?.orderNumber || '');
            if (renderedImageUrl) {
              return await renderCutoutCanvas(renderedImageUrl);
            }
          } catch (error) {
            // Fall through to placeholder as a last-resort visual.
          }

          usedLocalRenderer = true;
          return renderPlaceholderCanvas(item.primaryColor, item.secondaryColor, item.label || item.title || 'Item');
        })
      );

      function prepareBeltCanvas(src) {
        const cropped = cropCanvas(src, 0.04) || src;
        return resizeCanvasToMaxHeight(cropped, 800) || cropped;
      }

      function makeBeltItem(canvas, label, sourceOrderIndex) {
        const wrap = document.createElement('div');
        wrap.className = 'order-tracker__belt-item';
        wrap.dataset.itemName = label;
        wrap.dataset.orderIndex = String(sourceOrderIndex);
        wrap.setAttribute('aria-label', label);
        wrap.tabIndex = 0;
        wrap.appendChild(canvas);
        return wrap;
      }

      const frag = document.createDocumentFragment();
      canvases.forEach((c, i) => {
        const sourceItem = resolvedItems[i];
        frag.appendChild(
          makeBeltItem(
            prepareBeltCanvas(c),
            sourceItem.title || sourceItem.label || `Item ${i + 1}`,
            Number(sourceItem.sourceOrderIndex || 0)
          )
        );
      });
      beltItemsEl.appendChild(frag);
      wireConveyorListSelectionSync();

      if (beltLaneEl) {
        beltLaneEl.addEventListener('scroll', updateConveyorNavState, { passive: true });
        const selectedPalette = selectedOrderIndex >= 0
          ? buildConveyorHexPalette([currentOrderItems[selectedOrderIndex]])
          : [];
        const fallbackPalette = selectedPalette.length ? selectedPalette : buildConveyorHexPalette(currentOrderItems);
        startConveyorHexAnimation(beltLaneEl, fallbackPalette);
      }

      if (beltPrevEl) {
        beltPrevEl.addEventListener('click', () => {
          const laneWidth = beltLaneEl ? beltLaneEl.clientWidth : 0;
          scrollConveyorBy(-(laneWidth * 0.76 || 320));
        });
      }

      if (beltNextEl) {
        beltNextEl.addEventListener('click', () => {
          const laneWidth = beltLaneEl ? beltLaneEl.clientWidth : 0;
          scrollConveyorBy(laneWidth * 0.76 || 320);
        });
      }

      window.addEventListener('resize', updateConveyorNavState);

      // Keep only actual order count visible; do not duplicate loop items.
      requestAnimationFrame(() => {
        beltItemsEl.style.animation = 'none';
        beltItemsEl.style.transform = 'translateX(0)';
        updateConveyorNavState();
        if (conveyorStatusEl) {
          conveyorStatusEl.hidden = true;
          conveyorStatusEl.textContent = '';
        }
      });
    }

    if (prefillOrderNumber && !orderInput.value) {
      orderInput.value = prefillOrderNumber;
    }

    const stored = readStoredSession();
    if (stored && stored.orderNumber && stored.sessionToken) {
      if (String(stored.sessionToken).startsWith('internal-') && authEndpoint) {
        clearStoredSession();
        setPanelVisibility(false);
        setGateStatus('Your previous session was created in local fallback mode. Please request a new PIN and sign in again to use the live backend.');
        applyTrackerVisuals(fallbackStage, fallbackProgress);
        renderOrderItems([]);
        return;
      }

      if (String(stored.sessionToken).startsWith('dev-') || String(stored.sessionToken).startsWith('demo-')) {
        clearStoredSession();
        setPanelVisibility(false);
        setGateStatus('Development session cleared. Sign in again to load live data.');
        applyTrackerVisuals(fallbackStage, fallbackProgress);
        renderOrderItems([]);
        return;
      }

      startSession(stored);
      await hydratePanel();
    } else {
      setPanelVisibility(false);
      applyTrackerVisuals(fallbackStage, fallbackProgress);
      renderOrderItems([]);
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    const roots = document.querySelectorAll('[data-order-tracker-root]');
    roots.forEach((root) => {
      void initTracker(root);
    });
  });
})();
